import React, { useState, useRef } from 'react';
import { X, CheckCircle, Trash2, RefreshCw, Send, Eye, Ticket, AlertTriangle, ExternalLink, Copy, Clock, CalendarDays, Upload, Mail, SkipForward, Split, BadgeCheck, FlaskConical, Sparkles, Layers } from 'lucide-react';
import { resolveContaSicoob } from '../../src/lib/sicoobAccount';
import { resolveBancoGateway, cancelarCobrancaAsaas, prorrogarCobrancaAsaas, gerarBoletoGran, gerarPdfBoletoVigor, publicarPdfBoletoVigor, LayoutBoletoVigor } from '../../src/lib/gatewayResolver';
import { addDaysISO } from '../../src/lib/janelaCobranca';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import { Fatura } from '../../types';
import AnimatedBoletoIcon from './components/AnimatedBoletoIcon';
import { isPdfEspelho, canSubstitutePdf, isFaturaZerada, bloqueiaEnvioPorEspelho } from '../../src/lib/faturaUtils';
import LembretesViewer from './components/LembretesViewer';
import { ensureCobrancaCliente, resolveCpfCnpjCliente } from '../../src/lib/cobrancaCliente';
import { enviarCobrancaEmail } from '../../src/lib/cobrancaEmail';
import { normalizeUcCodigo } from '../../src/lib/ucCodigo';
import { adesaoNoEnvio, ROTULOS_ADESAO_NO_ENVIO, textoConviteAdesao, type AdesaoNoEnvio } from '../../src/lib/boletoUnico/adesaoNoEnvio';
import { resolverLinkBoleto } from '../../src/lib/linkBoleto';
import { faltaPdfUnificado } from '../../src/lib/pdfUnificadoGran';
import { boletoExisteNoGateway, MOTIVO_CANCELAMENTO_LOCAL } from '../../src/lib/emissaoBoletoValida';
import { faltaNoMotivo, textoDoMotivoCancelamento } from '../../src/lib/motivosCancelamentoBoleto';
import { CampoMotivoCancelamento, SEM_MOTIVO_CANCELAMENTO, type MotivoCancelamentoEscolhido } from './components/CampoMotivoCancelamento';
import TimelineCobranca from './TimelineCobranca';
import {
    checarEnvioCobranca,
    emailServeComoCanal,
    montarPreviaEnvio,
    temBloqueio,
    type ChecagemEnvio,
} from '../../src/lib/validacaoEnvioCobranca';

interface BoletoEmitidoModalProps {
    isOpen: boolean;
    onClose: () => void;
    fatura: Fatura | null;
    onUpdate: () => void;
    onParcelar?: (fatura: Fatura) => void;
    onQuitar?: (fatura: Fatura) => void;
    onSincronizarStatus?: (fatura: Fatura) => void;
}

const TELEFONE_MIN_DIGITOS = 10;

const formatTelefone = (valor: string) => {
    const limpo = valor.replace(/\D/g, '');
    let formatado = limpo.slice(0, 11);
    if (formatado.length > 2) formatado = `(${formatado.slice(0, 2)}) ${formatado.slice(2)}`;
    if (limpo.length > 7) formatado = `${formatado.slice(0, 10)}-${formatado.slice(10)}`;
    return formatado;
};

/**
 * Lista de destinos do WhatsApp, só dígitos e sem repetidos. O n8n dispara uma
 * mensagem por item — daí a deduplicação: os dois campos preenchidos com o mesmo
 * número mandariam o boleto duas vezes para a mesma pessoa.
 */
const montarTelefonesEnvio = (...numeros: string[]) => Array.from(new Set(
    numeros.map(n => n.replace(/\D/g, '')).filter(n => n.length >= TELEFONE_MIN_DIGITOS)
));


interface ContatoUc {
    whatsapp_cliente: string | null;
    whatsapp_cliente_2: string | null;
    email_cliente: string | null;
    receber_cobranca_email: boolean | null;
    // Exceção do bloqueio de espelho, ligada no cadastro da UC. Mora aqui porque
    // quem resolve a UC da fatura é esta função — repetir a busca só para ler a
    // flag traria o risco de as duas consultas caírem em cadastros diferentes.
    permite_envio_espelho: boolean | null;
    permite_envio_espelho_motivo: string | null;
    // Adesão ao boleto único: o selo da tela de envio sai desta mesma busca, pelo mesmo
    // motivo da flag de espelho — duas consultas poderiam cair em cadastros diferentes.
    situacao?: string | null;
    empresa_parceira_id?: string | number | null;
    boleto_unico?: boolean | null;
    boleto_unico_aceite_em?: string | null;
}

/**
 * Busca o WhatsApp/e-mail cadastrados na UC pra pré-preencher a tela de envio.
 *
 * `fatura.uc_codigo` vem do robô sem pontuação ("115545201238"), enquanto
 * `unidades_consumidoras.numero_uc` costuma estar cadastrado formatado
 * ("1.155.452.012-38") — comparar string crua (como antes) quase nunca batia,
 * e caía num fallback por CPF/CNPJ que, quando o documento é genérico
 * (ex.: CNPJ de associação usado como titular de dezenas de UCs de clientes
 * diferentes), pegava o contato de outra pessoa e mandava a cobrança pro
 * WhatsApp errado. Usa as colunas geradas `numero_uc_norm`/`outro_numero_uc_norm`
 * (migration 20260811151000, mesma normalização de src/lib/ucCodigo.ts) e só
 * confia no CPF/CNPJ quando ele aponta pra um único cadastro.
 */
const buscarContatoUcParaCobranca = async (
    ucCodigo?: string | null,
    cpfCnpj?: string | null
): Promise<ContatoUc | null> => {
    const ucNorm = normalizeUcCodigo(ucCodigo);

    if (ucNorm) {
        const { data, error } = await supabase
            .from('unidades_consumidoras')
            .select('whatsapp_cliente, whatsapp_cliente_2, email_cliente, receber_cobranca_email, permite_envio_espelho, permite_envio_espelho_motivo, situacao, empresa_parceira_id, boleto_unico, boleto_unico_aceite_em')
            .or(`numero_uc_norm.eq.${ucNorm},outro_numero_uc_norm.eq.${ucNorm}`)
            .limit(1);
        if (error) throw error;
        if (data && data.length > 0) return data[0];
    }

    const cleanCpfCnpj = cpfCnpj?.replace(/\D/g, '');
    if (cleanCpfCnpj) {
        const { data } = await supabase
            .from('unidades_consumidoras')
            .select('whatsapp_cliente, whatsapp_cliente_2, email_cliente, receber_cobranca_email, permite_envio_espelho, permite_envio_espelho_motivo, situacao, empresa_parceira_id, boleto_unico, boleto_unico_aceite_em')
            .eq('cpf_cnpj_cliente', cleanCpfCnpj)
            .limit(2);
        // Documento não-único = não dá pra saber qual cadastro é o certo; melhor
        // deixar em branco (a validação de envio já exige preenchimento manual)
        // do que arriscar autocompletar com o contato de outro cliente.
        if (data && data.length === 1) return data[0];
    }

    return null;
};

/**
 * Lista os problemas encontrados antes do envio. Bloqueio em vermelho (trava o
 * botão), alerta em âmbar (só avisa). Fica no topo da tela de propósito: o
 * operador precisa ver o impedimento antes de percorrer o formulário.
 */
const PainelChecagens: React.FC<{ bloqueios: ChecagemEnvio[]; alertas: ChecagemEnvio[] }> = ({ bloqueios, alertas }) => {
    if (bloqueios.length === 0 && alertas.length === 0) return null;

    return (
        <div className="mb-6 space-y-3">
            {bloqueios.length > 0 ? (
                <div className="p-4 bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 rounded-r-xl">
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg shrink-0">
                            <AlertTriangle size={18} />
                        </div>
                        <div className="flex-1 text-left">
                            <h4 className="text-sm font-bold text-red-800 dark:text-red-400">
                                {bloqueios.length === 1 ? 'Envio bloqueado' : `Envio bloqueado — ${bloqueios.length} pendências`}
                            </h4>
                            <ul className="mt-1.5 space-y-1">
                                {bloqueios.map((b, i) => (
                                    <li key={`${b.campo}-${i}`} className="text-xs text-red-700/90 dark:text-red-300/90 flex gap-1.5">
                                        <span aria-hidden="true">•</span>
                                        <span>{b.mensagem}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}

            {alertas.length > 0 ? (
                <div className="p-4 bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 rounded-r-xl">
                    <div className="flex items-start gap-3">
                        <div className="p-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-lg shrink-0">
                            <AlertTriangle size={18} />
                        </div>
                        <div className="flex-1 text-left">
                            <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">
                                {alertas.length === 1 ? 'Confira antes de enviar' : `Confira antes de enviar — ${alertas.length} pontos`}
                            </h4>
                            <ul className="mt-1.5 space-y-1">
                                {alertas.map((a, i) => (
                                    <li key={`${a.campo}-${i}`} className="text-xs text-amber-700/90 dark:text-amber-300/90 flex gap-1.5">
                                        <span aria-hidden="true">•</span>
                                        <span>{a.mensagem}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

/** Uma linha da prévia. `ausente` pinta o valor de vermelho e escreve o que
 *  falta — é assim que "sem código de barras" salta aos olhos. */
const LinhaPrevia: React.FC<{ rotulo: string; valor: string; mono?: boolean; ausente?: string }> = ({ rotulo, valor, mono, ausente }) => (
    <div className="flex flex-col gap-0.5">
        <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">{rotulo}</span>
        {valor ? (
            <span className={`text-sm text-gray-900 dark:text-gray-100 break-all ${mono ? 'font-mono text-xs' : ''}`}>{valor}</span>
        ) : (
            <span className="text-sm font-semibold text-red-600 dark:text-red-400">{ausente || '— não preenchido —'}</span>
        )}
    </div>
);

/**
 * Selo de adesão ao boleto único na tela de envio (pedido da Meyryene, 14/10/2026). O
 * envio é a hora em que a operação já está falando com o cliente; quem não aderiu ganha
 * ali uma abordagem humana e, depois dela, o convite com o passo a passo para colar no
 * WhatsApp. Não aparece para UC sem relação com o boleto único (parceira, cancelada).
 */
const TOM_ADESAO: Record<AdesaoNoEnvio, string> = {
    aderiu: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-900/20 dark:text-emerald-300',
    aceitou_aguardando: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-300',
    nao_aderiu: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-300',
};

const SeloAdesaoEnvio: React.FC<{ adesao: AdesaoNoEnvio | null; nomeCliente: string; onCopiado: () => void }> = ({ adesao, nomeCliente, onCopiado }) => {
    if (!adesao) return null;
    const rotulo = ROTULOS_ADESAO_NO_ENVIO[adesao];
    const copiar = async () => {
        try {
            await navigator.clipboard.writeText(textoConviteAdesao(nomeCliente));
            onCopiado();
        } catch {
            // Sem permissão de área de transferência (navegador antigo, http): mostra o texto
            // para a pessoa copiar na mão, em vez de falhar calado.
            window.prompt('Copie o convite abaixo:', textoConviteAdesao(nomeCliente));
        }
    };
    return (
        <div role="status" className={`mb-4 rounded-xl border px-4 py-3 ${TOM_ADESAO[adesao]}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="flex items-center gap-2 text-sm font-bold">
                    <Layers size={16} aria-hidden="true" />
                    {rotulo.texto}
                </p>
                {adesao === 'nao_aderiu' && (
                    <button
                        type="button"
                        onClick={copiar}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-white/80 dark:bg-gray-800 px-3 py-1.5 text-xs font-bold text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700 hover:bg-white dark:hover:bg-gray-700 transition-colors"
                    >
                        <Copy size={13} aria-hidden="true" />
                        Copiar convite de adesão
                    </button>
                )}
            </div>
            <p className="mt-1 text-xs opacity-90">{rotulo.ajuda}</p>
        </div>
    );
};

/**
 * Valor do título lido da linha digitável (47 dígitos): os 10 últimos são o valor em
 * centavos. É o que o banco cobra de verdade — os campos da fatura são o que o VigorHub
 * PEDIU, e podem divergir quando o fluxo de emissão mudou no meio do caminho.
 * Devolve 0 quando a linha não serve (vazia, tamanho errado, arrecadação iniciada em 8).
 */
const valorNaLinhaDigitavel = (linha?: string | null): number => {
    const d = String(linha || '').replace(/\D/g, '');
    if (d.length !== 47 || d[0] === '8') return 0;
    return Number(d.slice(37)) / 100;
};

/**
 * O boleto cobra também a conta da Equatorial (boleto único). Nesse caso o valor da
 * mensagem de envio é o total, e ele NÃO pode ir para `valor_boleto_vigor` — a parte da
 * Vigor, base da comissão. Foi o que inflou 11 faturas de 14 a 16/10/2026.
 */
const boletoCobraEquatorial = (f: Fatura): boolean =>
    Number(f.valor_equatorial_no_boleto || 0) > 0
    || valorNaLinhaDigitavel(f.linha_digitavel_boleto) - Number(f.valor_boleto_vigor || 0) > 0.005;

const BoletoEmitidoModal: React.FC<BoletoEmitidoModalProps> = ({ isOpen, onClose, fatura, onUpdate, onParcelar, onQuitar, onSincronizarStatus }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const backdropMouseDownRef = useRef(false);
    // Fatura em que o envio foi aberto (#6480). O modal fica montado entre uma fatura e
    // outra: as confirmações gravam nela, nunca na que estiver na tela no clique final.
    const faturaDoEnvioRef = useRef<Fatura | null>(null);
    const faturaAberta = fatura;
    const [loading, setLoading] = useState(false);
    const [cancelConfirmation, setCancelConfirmation] = useState(false);
    // Motivo em lista fechada (#6509): o texto gravado começa pelo rótulo do motivo.
    const [motivoCancel, setMotivoCancel] = useState<MotivoCancelamentoEscolhido>(SEM_MOTIVO_CANCELAMENTO);
    const cancelReason = textoDoMotivoCancelamento(motivoCancel.codigo, motivoCancel.detalhe);
    // Prorrogação da data limite de pagamento (sem reemitir o boleto)
    const [prorrogarConfirmation, setProrrogarConfirmation] = useState(false);
    const [novaDataLimite, setNovaDataLimite] = useState('');
    const [replacingPdfLoading, setReplacingPdfLoading] = useState(false);
    
    // States for send confirmation modal
    const [sendConfirmation, setSendConfirmation] = useState(false);
    const [sendSkipConfirmation, setSendSkipConfirmation] = useState(false);
    const [showSkipSendScreen, setShowSkipSendScreen] = useState(false);
    const [skipSendReason, setSkipSendReason] = useState('');
    const [sendNomeCliente, setSendNomeCliente] = useState('');
    const [sendTelefoneCliente, setSendTelefoneCliente] = useState('');
    const [sendTelefoneCliente2, setSendTelefoneCliente2] = useState('');
    const [sendEmailCliente, setSendEmailCliente] = useState('');
    const [sendMesReferencia, setSendMesReferencia] = useState('');
    const [sendValorBoleto, setSendValorBoleto] = useState('');
    const [adesaoEnvio, setAdesaoEnvio] = useState<AdesaoNoEnvio | null>(null);
    const [sendVencimentoBoleto, setSendVencimentoBoleto] = useState('');
    const [sendCodigoPix, setSendCodigoPix] = useState('');
    const [sendLinhaDigitavel, setSendLinhaDigitavel] = useState('');

    // UC marcada para receber a cobrança também por e-mail; preenchido pelo
    // efeito mais abaixo. Fica aqui com os outros campos de envio porque as
    // travas e a prévia leem este valor.
    const [receberEmail, setReceberEmail] = useState(false);

    // Cliente autorizado a receber o espelho da distribuidora (a fatura sem PIX
    // nem código de barras). Preenchido pelo mesmo efeito que lê a preferência de
    // e-mail. Começa desligado de propósito: enquanto a UC não respondeu, o envio
    // segue bloqueado — soltar o botão antes da resposta deixaria o espelho sair
    // no clique rápido.
    const [ucPermiteEspelho, setUcPermiteEspelho] = useState(false);
    const [ucMotivoEspelho, setUcMotivoEspelho] = useState<string | null>(null);

    // Segunda etapa do envio: a prévia do que o cliente vai receber. Fica atrás
    // de um passo próprio (e não junto do formulário) porque o ponto é olhar a
    // mensagem pronta, sem os campos de edição competindo pela atenção.
    const [showPreview, setShowPreview] = useState(false);

    // As telas de confirmação (enviar, pular envio, cancelar, prorrogar) são da fatura em
    // que foram abertas. O modal fica montado entre uma fatura e outra: se uma delas
    // ficasse aberta quando a fatura sai da tela, a próxima abriria nela, com o telefone
    // e o e-mail do cliente anterior nos campos de envio.
    React.useEffect(() => {
        setCancelConfirmation(false);
        setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
        setProrrogarConfirmation(false);
        setNovaDataLimite('');
        setSendConfirmation(false);
        setSendSkipConfirmation(false);
        setShowSkipSendScreen(false);
        setSkipSendReason('');
        setShowPreview(false);
    }, [isOpen, fatura?.id]);

    // E-mails que o banner de "envio adicional por e-mail" mostra ao operador —
    // acompanha ao vivo o que está digitado no campo de e-mail da tela. A cobrança
    // vai para um único destinatário: o segundo e-mail saiu da tela de envio.
    const emailsDestino = React.useMemo(
        () => [sendEmailCliente].map(e => e.trim()).filter(Boolean),
        [sendEmailCliente]
    );

    // Cobrança da Gran criada no Asaas sem o PDF unificado (fatura 14510, 20/10/2026).
    // Trava o envio, troca o "Visualizar" por um aviso e põe o botão de gerar à vista.
    const semPdfUnificado = faltaPdfUnificado(fatura);

    // Dados brutos da tela de envio, num objeto só — é o que alimenta tanto as
    // travas quanto a prévia, garantindo que as duas olhem exatamente o mesmo.
    const dadosEnvio = React.useMemo(() => ({
        nomeCliente: sendNomeCliente,
        telefone: sendTelefoneCliente,
        telefone2: sendTelefoneCliente2,
        email: sendEmailCliente,
        mesReferencia: sendMesReferencia,
        valorBoleto: sendValorBoleto,
        vencimento: sendVencimentoBoleto,
        codigoPix: sendCodigoPix,
        linhaDigitavel: sendLinhaDigitavel,
        receberEmail,
        bloqueadoDuplicata: fatura?.bloqueado_envio_duplicata,
        faltaPdfUnificado: semPdfUnificado,
    }), [
        sendNomeCliente, sendTelefoneCliente, sendTelefoneCliente2, sendEmailCliente,
        sendMesReferencia, sendValorBoleto, sendVencimentoBoleto, sendCodigoPix,
        sendLinhaDigitavel, receberEmail, fatura?.bloqueado_envio_duplicata, semPdfUnificado,
    ]);

    // Trava de espelho, calculada uma vez: os botões, o banner e os títulos de
    // ajuda precisam responder a mesma coisa. `fatura` pode ser null enquanto o
    // modal fecha.
    const envioBloqueadoPorEspelho = React.useMemo(
        () => !!fatura && bloqueiaEnvioPorEspelho(fatura, { ucPermiteEspelho }),
        [fatura, ucPermiteEspelho],
    );

    const checagensEnvio = React.useMemo(() => checarEnvioCobranca(dadosEnvio), [dadosEnvio]);
    const bloqueiosEnvio = React.useMemo(
        () => checagensEnvio.filter(c => c.severidade === 'bloqueio'),
        [checagensEnvio]
    );
    const alertasEnvio = React.useMemo(
        () => checagensEnvio.filter(c => c.severidade === 'alerta'),
        [checagensEnvio]
    );
    const envioBloqueado = bloqueiosEnvio.length > 0;
    const previaEnvio = React.useMemo(
        () => montarPreviaEnvio(dadosEnvio, fatura?.uc_codigo),
        [dadosEnvio, fatura?.uc_codigo]
    );

    /** Marca os inputs com problema — o operador vê o campo, não só a lista.
     *  `canalEnvio` (nenhum canal escolhido) não tem input próprio: acende o
     *  telefone e o e-mail juntos, que são as duas saídas possíveis. */
    const campoTemBloqueio = React.useCallback(
        (campo: ChecagemEnvio['campo']) => bloqueiosEnvio.some(b =>
            b.campo === campo ||
            (b.campo === 'canalEnvio' && (campo === 'telefone' || campo === 'email'))
        ),
        [bloqueiosEnvio]
    );

    /** O envio por WhatsApp só acontece com número utilizável; sem ele a
     *  cobrança sai apenas por e-mail (a trava acima garante que ao menos um
     *  dos dois existe). */
    const temWhatsappParaEnvio = React.useMemo(
        () => montarTelefonesEnvio(sendTelefoneCliente, sendTelefoneCliente2).length > 0,
        [sendTelefoneCliente, sendTelefoneCliente2]
    );

    /** Tela de comunicação (fatura que pulou o cálculo): sem WhatsApp e sem
     *  e-mail utilizável não há por onde enviar. Ela não usa `checarEnvioCobranca`
     *  — não tem boleto, valor nem vencimento para checar. */
    const comunicacaoSemCanal = !temWhatsappParaEnvio && !emailServeComoCanal(receberEmail, sendEmailCliente);

    // Boleto único (Gran): quando existe o PDF combinado (layout da Gran + fatura da
    // Equatorial com marca d'água), ele é o que deve ser mostrado — não o link genérico
    // do Asaas. Qual das origens vale (site institucional, Storage, gateway) é
    // decisão única de resolverLinkBoleto.
    const handleVisualizarBoleto = async () => {
        // Sem o PDF unificado, resolverLinkBoleto cairia no link do Asaas — que não é
        // o boleto que o cliente deve receber.
        if (semPdfUnificado) {
            addToast('O PDF unificado deste boleto ainda não foi gerado. Clique em "Gerar PDF unificado".', 'error', 8000);
            return;
        }
        let link = await resolverLinkBoleto(fatura);
        if (!link) {
            addToast('Erro ao gerar link do PDF do boleto.', 'error');
            return;
        }
        // Cache bust para garantir que o navegador baixe o PDF mais recente (caso tenha sido regerado)
        link = `${link}${link.includes('?') ? '&' : '?'}cb=${Date.now()}`;
        window.open(link, '_blank');
    };

    // Redesenha o PDF do boleto com o layout atual. Só para admin: é ferramenta de
    // manutenção (reconferir o desenho depois de mexer no layout), não parte do fluxo
    // de cobrança. Em nenhum dos dois caminhos mexe na cobrança em si — nada de Asaas,
    // Sicoob ou valores.
    //
    // Os dois caminhos são diferentes de propósito:
    //   Gran  -> o PDF combinado fica salvo no Storage, então regerar sobrescreve o
    //            arquivo e o "Visualizar Boleto / PDF" passa a mostrar o novo.
    //   Vigor -> não existe PDF salvo; a function desenha na hora e devolve os bytes,
    //            então o resultado é aberto direto numa aba.
    const [regenerandoPdf, setRegenerandoPdf] = useState(false);
    // Qual layout está sendo desenhado agora — os botões dividem o mesmo `loading`,
    // então sem isto os dois mostrariam "Gerando..." ao mesmo tempo.
    const [layoutEmGeracao, setLayoutEmGeracao] = useState<LayoutBoletoVigor | null>(null);
    // Só vale para o layout v6 nas faturas de teste: o selo "Bom Pagador" tem regra
    // própria (histórico limpo), e a fatura-exemplo nunca a satisfaz — sem este
    // controle não daria para ver o cabeçalho com selo.
    const [seloBomPagador, setSeloBomPagador] = useState(true);
    const ehBoletoGran = !!fatura?.empresa_parceira_id;
    const podeRegerarPdf = !!fatura?.id;
    // Layout v6 (boleto único) só para adm. Flag própria de propósito: dividia
    // `podeRegerarPdf`, e quando a regeração foi aberta a todos (e5cc1c0, 22/09/2026)
    // o botão do v6 abriu junto.
    const podeVerLayoutV6 = profile?.role === 'adm' && !!fatura?.id;

    // Cobranca viva no Sicoob (nosso numero + linha digitavel) e nenhum PDF para
    // abrir. E o estado que o fluxo n8n de emissao deixa quando o desenho do PDF
    // falha DEPOIS de registrar o boleto -- a cobranca existe, o cliente pode pagar,
    // mas a tela nao tem o que mostrar nem o que enviar. Nao vale para boleto de
    // teste (nao existe no banco) nem para parceira (la o PDF e o combinado, montado
    // pela gerar-boleto-gran).
    const [publicandoPdf, setPublicandoPdf] = useState(false);
    const pdfPerdido = !!fatura
        && !fatura.url_boleto
        && !fatura.url_boleto_combinado
        && !fatura.pdf_boleto_combinado
        && !!fatura.num_cobranca_boleto
        && !!fatura.linha_digitavel_boleto
        && !fatura.motivo_pular_calculo
        && fatura.ambiente !== 'sandbox'
        && !ehBoletoGran;

    /** Redesenha o boleto, publica no site e grava `url_boleto`. Nao mexe na cobranca:
     *  nenhum valor, nosso numero ou vencimento muda, e o banco nem e chamado. */
    const handlePublicarPdfPerdido = async () => {
        if (!fatura?.id) return;
        setPublicandoPdf(true);
        try {
            await publicarPdfBoletoVigor(fatura.id);
            await logAction(user?.id || 'SYSTEM', 'PUBLISH_MISSING_BOLETO_PDF', {
                fatura_id: fatura.id,
                num_cobranca_boleto: fatura.num_cobranca_boleto,
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'publicar_pdf_boleto', origem: 'acao' });
            addToast('PDF do boleto gerado e vinculado a fatura.', 'success');
            onUpdate();
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setPublicandoPdf(false);
        }
    };

    /** Abre numa aba o PDF desenhado na hora pela edge function. É só desenho: não toca
     *  na cobrança (Sicoob/Asaas), no valor nem no que já foi enviado ao cliente.
     *  `layout: 'v6'` é o boleto unificado novo, ainda em avaliação. */
    const abrirPdfBoletoVigor = async (layout: LayoutBoletoVigor, selo?: boolean) => {
        if (!fatura?.id) return;
        setRegenerandoPdf(true);
        setLayoutEmGeracao(layout);
        try {
            const blob = await gerarPdfBoletoVigor(fatura.id, layout, selo);
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            // Revoga depois de um tempo: revogar na hora quebraria a aba recém-aberta.
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            addToast(
                layout === 'v6' ? 'Boleto gerado no layout novo (v6).' : 'PDF gerado com o layout atual.',
                'success',
            );
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'regerar_pdf_boleto', origem: 'acao' });
            await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                fatura_id: fatura.id,
                origem: 'vigor',
                layout,
                selo,
            });
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setRegenerandoPdf(false);
            setLayoutEmGeracao(null);
        }
    };

    /** Baixa o PDF do boleto de teste. Ele não existe como arquivo publicado (não passou
     *  pelo n8n), então é desenhado na hora e entregue como download. */
    const handleBaixarPdfTeste = async (layout: LayoutBoletoVigor = 'legado', selo?: boolean) => {
        if (!fatura?.id) return;
        setRegenerandoPdf(true);
        setLayoutEmGeracao(layout);
        try {
            const blob = await gerarPdfBoletoVigor(fatura.id, layout, selo);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `boleto_teste_${layout === 'v6' ? 'v6_' : ''}${fatura.id}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            addToast('PDF de teste baixado.', 'success');
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setRegenerandoPdf(false);
            setLayoutEmGeracao(null);
        }
    };

    const handleRegerarPdf = async () => {
        if (!fatura?.id) return;
        setRegenerandoPdf(true);
        try {
            if (!ehBoletoGran) {
                await publicarPdfBoletoVigor(fatura.id);
                addToast('PDF regerado e publicado com sucesso.', 'success');
                await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                    fatura_id: fatura.id,
                    origem: 'vigor',
                });
            } else {
                const resultado = await gerarBoletoGran(fatura.id);
                if (resultado?.aviso) {
                    addToast(resultado.aviso, 'warning', 8000);
                } else {
                    addToast('PDF regerado. Abra "Visualizar Boleto / PDF" para conferir.', 'success');
                }
                await logAction(user?.id || 'SYSTEM', 'REGENERATE_BOLETO_PDF', {
                    fatura_id: fatura.id,
                    origem: 'gran',
                });
            }
            onUpdate();
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'regerar_pdf_boleto', origem: 'acao' });
        } catch (error: any) {
            addToast(error.message || 'Erro ao gerar o PDF do boleto.', 'error');
        } finally {
            setRegenerandoPdf(false);
        }
    };

    // (o state `receberEmail` é declarado junto dos demais campos de envio, lá em
    //  cima — as travas e a prévia dependem dele e são calculadas antes daqui)
    React.useEffect(() => {
        if (!isOpen || (!fatura?.uc_codigo && !fatura?.cliente_cpf_cnpj)) return;
        const checkEmailPreference = async () => {
            try {
                // Mesmo resolvedor do pré-preenchimento de contato. Antes esta
                // consulta comparava numero_uc cru com fatura.uc_codigo, que casava
                // em 5 de 41 faturas da Gran — nas outras 36 a tela concluía "esta
                // UC não quer e-mail", escondia os campos e o envio por e-mail nem
                // era tentado, sem erro nenhum aparecer.
                const contato = await buscarContatoUcParaCobranca(fatura.uc_codigo, fatura.cliente_cpf_cnpj);
                setReceberEmail(contato?.receber_cobranca_email ?? false);
                setUcPermiteEspelho(contato?.permite_envio_espelho ?? false);
                setUcMotivoEspelho(contato?.permite_envio_espelho_motivo ?? null);
            } catch (err) {
                console.error("Erro ao buscar preferência de e-mail:", err);
                setReceberEmail(false);
                setUcPermiteEspelho(false);
                setUcMotivoEspelho(null);
            }
        };
        checkEmailPreference();
    }, [isOpen, fatura?.uc_codigo, fatura?.cliente_cpf_cnpj]);

    const handleReplacePdf = async (file: File) => {
        if (!fatura) return;
        setReplacingPdfLoading(true);
        try {
            const { data: webhookData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'pdf_definitivo_webhook_url')
                .single();

            const webhookUrl = webhookData?.value;

            if (!webhookUrl) {
                throw new Error('Webhook de substituição de PDF não configurado em APIs e Webhooks.');
            }

            const safeFilename = `${(fatura.mes_referencia || 'fatura').replace('/', '_')}-${Date.now()}.pdf`;

            const formData = new FormData();
            formData.append('fatura_padrao', file, safeFilename);
            formData.append('fatura_id', fatura.id);
            formData.append('uc_codigo', fatura.uc_codigo || '');
            formData.append('conta_mes', fatura.mes_referencia || '');
            formData.append('file_name', safeFilename);

            const uploadResponse = await fetch(webhookUrl, {
                method: 'POST',
                body: formData,
            });

            if (!uploadResponse.ok) {
                throw new Error(`Erro no webhook: ${uploadResponse.status}`);
            }

            addToast('PDF definitivo substituído com sucesso!', 'success');
            onUpdate();
            onClose();
        } catch (error: any) {
            addToast(error.message || 'Erro ao substituir PDF.', 'error');
        } finally {
            setReplacingPdfLoading(false);
        }
    };

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        addToast(`${label} copiado!`, 'success');
    };

    const handlePreSendToClient = async () => {
        if (!fatura) return;
        faturaDoEnvioRef.current = fatura;

        if (fatura.bloqueado_envio_duplicata) {
            addToast(
                'Envio bloqueado: esta fatura possui uma duplicata pendente de resolução. Acesse o painel de faturas, identifique a duplicata e exclua a incorreta antes de enviar.',
                'error'
            );
            return;
        }

        setSendNomeCliente(fatura.nome_cliente_boleto || fatura.cliente_nome || '');
        setSendTelefoneCliente(''); // Initialize empty
        setSendTelefoneCliente2('');
        setSendEmailCliente('');
        setSendMesReferencia(fatura.mes_referencia || '');
        // O valor da mensagem é o que o cliente vai pagar, não a parte da Vigor: no boleto
        // único o Sicoob registra Vigor + Equatorial em `valor_emissao_boleto_unico`, e o
        // WhatsApp dizendo R$ 187,55 para um boleto de R$ 339,58 faria o cliente achar que
        // houve erro. Só troca quando o emitido é maior; nos demais casos os dois são iguais.
        const valorVigor = Number(fatura.valor_boleto_vigor || 0);
        // O que o Sicoob registrou, lido da linha digitável (mesmo motivo do `boleto_unico`
        // do payload de envio). Sem linha válida, cai no valor gravado na emissão.
        const valorEmitido = valorNaLinhaDigitavel(fatura.linha_digitavel_boleto)
            || Number(fatura.valor_emissao_boleto_unico || 0);
        const valorCobrado = valorEmitido - valorVigor > 0.005 ? valorEmitido : valorVigor;
        setSendValorBoleto(valorCobrado > 0 ? valorCobrado.toFixed(2) : '');
        setSendVencimentoBoleto(fatura.vencimento_boleto || fatura.data_vencimento || '');
        setSendCodigoPix(fatura.codigo_pix_boleto || '');
        setSendLinhaDigitavel(fatura.linha_digitavel_boleto || '');
        setShowSkipSendScreen(false);
        setSkipSendReason('');
        setShowPreview(false);
        setSendConfirmation(true);
        setAdesaoEnvio(null);

        // Fetch client details from `unidades_consumidoras` table
        if (fatura.uc_codigo || fatura.cliente_cpf_cnpj) {
            try {
                const contato = await buscarContatoUcParaCobranca(fatura.uc_codigo, fatura.cliente_cpf_cnpj);
                if (faturaDoEnvioRef.current?.id !== fatura.id) return; // já é outra fatura: não preenche com o contato desta
                if (contato) {
                    if (contato.whatsapp_cliente) setSendTelefoneCliente(contato.whatsapp_cliente);
                    if (contato.whatsapp_cliente_2) setSendTelefoneCliente2(contato.whatsapp_cliente_2);
                    if (contato.email_cliente) setSendEmailCliente(contato.email_cliente);
                    setAdesaoEnvio(fatura.empresa_parceira_id ? null : adesaoNoEnvio(contato));
                } else {
                    addToast('Não encontrei o WhatsApp/e-mail cadastrado desta UC automaticamente. Confira e preencha antes de enviar.', 'info');
                }
            } catch (err) {
                console.log('Error fetching client details from unidades_consumidoras:', err);
            }
        }
    };

    // Garante que o cliente entre na esteira de acompanhamento de cobrança
    // (carteira fixa por analista) assim que a fatura vira "Cobrança enviada".
    // Best-effort: nunca deve travar/reverter o envio já confirmado.
    const iniciarCobrancaCliente = async (nomeCliente: string | undefined, alvo: Fatura | null) => {
        try {
            const cpfCnpj = await resolveCpfCnpjCliente(alvo?.uc_codigo, alvo?.cliente_cpf_cnpj);
            if (cpfCnpj) {
                await ensureCobrancaCliente(cpfCnpj, nomeCliente || alvo?.cliente_nome);
            }
        } catch (err) {
            console.error('Error ensuring cobranca_clientes row:', err);
        }
    };

    const handleConfirmSendToClient = async () => {
        const fatura = faturaDoEnvioRef.current ?? faturaAberta;
        if (!fatura) return;

        // Trava de verdade, não só a cor do botão: o botão fica desabilitado
        // quando há bloqueio, mas esta checagem é o que garante que nenhum
        // caminho (atalho de teclado, clique antes do re-render, chamada futura
        // de outro lugar) dispare o webhook com a cobrança incompleta. Foi
        // exatamente uma cobrança sem código de barras e sem PIX que chegou ao
        // Portal dos Buritis em 17/08/2026.
        if (envioBloqueado) {
            addToast(bloqueiosEnvio[0].mensagem, 'error', 8000);
            return;
        }

        setLoading(true);

        try {
            const telefonesEnvio = montarTelefonesEnvio(sendTelefoneCliente, sendTelefoneCliente2);

            // As duas leituras são independentes: em série somariam dois round
            // trips antes de qualquer envio.
            const [{ data: webhookData }, { data: tokenRow }] = await Promise.all([
                supabase.from('system_settings').select('value').eq('key', 'boleto_send_webhook_url').single(),
                // O token monta o link do botão "Ver boleto" no template WhatsApp.
                // Buscado aqui, e não lido de `fatura`, porque cada tela que abre
                // este modal seleciona um conjunto diferente de colunas.
                supabase.from('faturas').select('token_boleto_publico').eq('id', fatura.id).maybeSingle(),
            ]);

            const webhookUrl = webhookData?.value;

            // Cliente sem WhatsApp (só e-mail): não há webhook a acionar. Chamar
            // o n8n com `telefone_cliente` vazio devolveria 200 sem mandar nada
            // — o operador veria "enviado com sucesso" sem envio nenhum.
            const enviarPorWhatsapp = telefonesEnvio.length > 0;

            if (enviarPorWhatsapp && !webhookUrl) {
                addToast('Webhook de envio não configurado.', 'error');
                return;
            }

            const payload = {
                fatura_id: fatura.id,
                // O template WABA mostra a UC na mensagem, então ela precisa chegar
                // ao n8n — nenhum outro campo do payload carrega esse dado.
                uc_codigo: fatura.uc_codigo,
                nome_cliente_boleto: sendNomeCliente,
                // telefone_cliente continua sendo o primeiro número: o n8n só usa
                // telefones_cliente quando ele vem, e outros consumidores do mesmo
                // webhook seguem lendo o campo antigo.
                telefone_cliente: telefonesEnvio[0],
                telefones_cliente: telefonesEnvio,
                email_cliente: sendEmailCliente.trim() || null,
                mes_referencia: sendMesReferencia,
                valor_boleto_vigor: sendValorBoleto ? Number(sendValorBoleto).toFixed(2) : '',
                vencimento_boleto: sendVencimentoBoleto,
                codigo_pix_boleto: sendCodigoPix,
                linha_digitavel_boleto: sendLinhaDigitavel,
                pdf_fatura: fatura.pdf_fatura,
                url_boleto: fatura.url_boleto,
                token_boleto_publico: tokenRow?.token_boleto_publico ?? null,
                // Escolhe o template no n8n. O critério é o BOLETO, não a adesão da UC:
                // só vale quando a cobrança emitida de fato inclui a Equatorial. UC liberada
                // depois da emissão (Vilmar, OUT/2026) tem boleto só com a parte da Vigor —
                // mandar "não pague a Equatorial" para ela deixaria a conta de luz sem pagar.
                // Fatura de empresa parceira segue o fluxo próprio dela.
                // O valor vem da LINHA DIGITÁVEL, que é o que o Sicoob de fato registrou —
                // não de `valor_emissao_boleto_unico`, que o VigorHub grava antes de chamar o
                // n8n. Em 14/10/2026 as faturas 13684, 13947 e 14016 foram emitidas antes da
                // correção do fluxo: o banco diz "somado", o Sicoob cobrou só a Vigor.
                boleto_unico: !fatura.empresa_parceira_id
                    && valorNaLinhaDigitavel(fatura.linha_digitavel_boleto) - Number(fatura.valor_boleto_vigor || 0) > 0.005,
                // Primeiro nome, só a inicial maiúscula: o cadastro vem em CAIXA ALTA e
                // "Olá, VILMAR" soa como cobrança automática. A UaisyChat usa o nome
                // salvo no contato em {{nome}}, por isso a saudação vai em campo próprio.
                nome_saudacao: (() => {
                    const primeiro = (sendNomeCliente || '').trim().split(/\s+/)[0] || '';
                    return primeiro ? primeiro[0].toUpperCase() + primeiro.slice(1).toLowerCase() : '';
                })(),
            };

            if (enviarPorWhatsapp) {
                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao acionar webhook (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }
            }

            await supabase.from('faturas').update({
                status_fatura: 'Cobrança enviada',
                // Atualizar os dados também na fatura para refletir as edições se houver
                nome_cliente_boleto: sendNomeCliente,
                mes_referencia: sendMesReferencia,
                // No boleto único o valor da mensagem inclui a Equatorial: a parte da Vigor fica.
                ...(boletoCobraEquatorial(fatura) ? {} : { valor_boleto_vigor: Number(sendValorBoleto) }),
                vencimento_boleto: sendVencimentoBoleto,
                codigo_pix_boleto: sendCodigoPix,
                linha_digitavel_boleto: sendLinhaDigitavel,
                enviado_por_nome: profile?.full_name || user?.email || 'Sistema',
                enviado_em: new Date().toISOString(),
            }).eq('id', fatura.id);

            await logAction(user?.id || 'SYSTEM', 'SEND_BOLETO_CLIENT', { fatura_id: fatura.id, payload });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'enviar_boleto_cliente', origem: 'acao' });

            // Persistência na tabela unidades_consumidoras (Upsert por numero_uc)
            //
            // Fatura de acordo de parcelamento fica de fora: com responsável pela
            // dívida, o nome e os contatos do envio são DELE, e gravar na UC trocava
            // o cliente e o WhatsApp do titular. Mesma regra da emissão
            // (BoletoEmissionModal, "Sync customer data").
            //
            // #6239: sem a permissão "Editar dados cadastrais", o envio não grava
            // nome/CPF/contatos na UC (o banco barraria); os contatos digitados valem
            // só para este envio.
            if (fatura.uc_codigo && !fatura.parcelamento_origem_id && podeEditarCadastro(profile)) {
                try {
                    const cleanCpfCnpj = (fatura.cliente_cpf_cnpj || '').replace(/\D/g, '');
                    
                    // Buscar se a UC já existe
                    const { data: ucList } = await supabase
                        .from('unidades_consumidoras')
                        .select('id, numero_uc, outro_numero_uc')
                        .or(`numero_uc.eq.${fatura.uc_codigo},outro_numero_uc.eq.${fatura.uc_codigo}`);

                    const findBestUC = (list: any[], code: string) => {
                        if (!list || list.length === 0) return null;
                        const primaryMatch = list.find(uc => uc.numero_uc === code);
                        if (primaryMatch) return primaryMatch;
                        const secondaryMatch = list.find(uc => uc.outro_numero_uc === code);
                        if (secondaryMatch) return secondaryMatch;
                        return list[0];
                    };

                    const existingUc = findBestUC(ucList || [], fatura.uc_codigo);

                    const ucData: any = {
                        numero_uc: fatura.uc_codigo,
                        nome_cliente: sendNomeCliente,
                        cpf_cnpj_cliente: cleanCpfCnpj,
                        // outro_email fica de fora de propósito: o campo saiu da tela,
                        // então gravar aqui apagaria o segundo e-mail já cadastrado na UC.
                        email_cliente: sendEmailCliente.trim() || null
                    };

                    // Só grava telefone quando há telefone. Numa cobrança que foi
                    // só por e-mail os campos estão vazios, e escrevê-los apagaria
                    // o WhatsApp já cadastrado na UC.
                    if (enviarPorWhatsapp) {
                        ucData.whatsapp_cliente = sendTelefoneCliente; // Salva formatado como no restante do sistema
                        ucData.whatsapp_cliente_2 = sendTelefoneCliente2.trim() || null;
                    }

                    if (existingUc) {
                        // Atualiza existente
                        await supabase
                            .from('unidades_consumidoras')
                            .update(ucData)
                            .eq('id', existingUc.id);
                        // Não criamos UC nova silenciosamente aqui. 
                        // Se não existe, apenas enviamos o webhook, a UC deve ser corrigida/cadastrada nos modais de emissão.
                        console.warn('UC não encontrada para atualizar contatos. Pulando criação automática.');
                    }
                } catch (err) {
                    console.log('Error upserting unidades_consumidoras:', err);
                }
            }

            await iniciarCobrancaCliente(sendNomeCliente, fatura);

            if (enviarPorWhatsapp) {
                addToast('Boleto enviado para o cliente com sucesso!', 'success');
            } else {
                addToast('Sem WhatsApp cadastrado — a cobrança vai apenas por e-mail.', 'info');
            }

            // Envio adicional por e-mail (Resend), só quando a UC pediu
            // (receberEmail). Roda DEPOIS que o UPDATE acima e a atualização da
            // UC já foram commitados, e depois do toast de sucesso do WhatsApp —
            // assim a ordem dos toasts acompanha a ordem real dos dois envios, e
            // a function relê a fatura já com os valores editados pelo operador.
            // enviarCobrancaEmail nunca lança; o try/catch aqui é só cinto e
            // suspensório: uma falha de e-mail NUNCA pode cair no catch abaixo,
            // que sugeriria ao operador que o envio inteiro falhou.
            if (receberEmail) {
                try {
                    const resultado = await enviarCobrancaEmail(fatura.id, { destinatarios: emailsDestino });
                    const destinatariosEnviados = resultado.destinatarios || [];
                    const warningsEnvio = resultado.warnings || [];
                    if (resultado.ok) {
                        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'enviar_cobranca_email', origem: 'acao' });
                        await logAction(user?.id || 'SYSTEM', 'SEND_COBRANCA_EMAIL', {
                            fatura_id: fatura.id,
                            destinatarios: destinatariosEnviados,
                            anexos: resultado.anexos || [],
                            warnings: warningsEnvio,
                        });
                        if (warningsEnvio.some(w => w.startsWith('boleto_pdf_'))) {
                            addToast('E-mail enviado sem o PDF do boleto — não foi possível gerá-lo agora. O PIX e a linha digitável foram no corpo da mensagem.', 'warning', 8000);
                        } else if (warningsEnvio.some(w => w.startsWith('fatura_pdf_'))) {
                            addToast('E-mail enviado sem o PDF da fatura da distribuidora. O boleto, o PIX e a linha digitável foram junto.', 'warning', 8000);
                        } else {
                            addToast(`E-mail da cobrança enviado para ${destinatariosEnviados.join(', ')}.`, 'success');
                        }
                    } else if (resultado.motivo === 'opt_out') {
                        addToast('E-mail não enviado: esta UC não está marcada para receber cobrança por e-mail.', 'info');
                    } else if (resultado.motivo === 'ja_enviado_recentemente') {
                        addToast('O e-mail desta cobrança já havia sido enviado há poucos minutos — nenhum novo e-mail foi disparado.', 'info');
                    } else {
                        await logAction(user?.id || 'SYSTEM', 'SEND_COBRANCA_EMAIL_FALHOU', {
                            fatura_id: fatura.id,
                            motivo: resultado.motivo,
                            mensagem: resultado.mensagem,
                        });
                        addToast(
                            enviarPorWhatsapp
                                ? `A cobrança foi enviada no WhatsApp, mas o e-mail falhou: ${resultado.mensagem}. A fatura já está em "Cobrança enviada".`
                                : `O e-mail falhou e não havia WhatsApp: ${resultado.mensagem}. O cliente NÃO recebeu a cobrança — a fatura está em "Cobrança enviada", tente de novo.`,
                            'warning',
                            12000,
                            { label: 'Tentar de novo', onClick: () => { enviarCobrancaEmail(fatura.id, { forcar: true }); } }
                        );
                    }
                } catch (emailErr) {
                    // Não deveria acontecer (enviarCobrancaEmail não lança), mas se
                    // acontecer, é só mais um aviso — o envio principal já está feito.
                    console.error('Unexpected error sending cobrança e-mail:', emailErr);
                }
            }

            setSendConfirmation(false);
            setShowPreview(false);
            onUpdate();
            onClose();

        } catch (error: any) {
            console.error('Error sending boleto:', error);
            addToast(error.message || 'Erro ao enviar boleto.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSkipSend = async () => {
        const fatura = faturaDoEnvioRef.current ?? faturaAberta;
        if (!fatura || !skipSendReason.trim()) return;
        setLoading(true);
        try {
            await supabase.from('faturas').update({
                status_fatura: 'Cobrança enviada',
                nome_cliente_boleto: sendNomeCliente,
                mes_referencia: sendMesReferencia,
                valor_boleto_vigor: sendValorBoleto && !boletoCobraEquatorial(fatura) ? Number(sendValorBoleto) : undefined,
                vencimento_boleto: sendVencimentoBoleto || undefined,
                codigo_pix_boleto: sendCodigoPix || undefined,
                linha_digitavel_boleto: sendLinhaDigitavel || undefined,
                enviado_por_nome: profile?.full_name || user?.email || 'Sistema',
                enviado_em: new Date().toISOString(),
                motivo_pular_envio: skipSendReason.trim(),
                pular_envio_por_nome: profile?.full_name || user?.email || 'Sistema',
                pular_envio_em: new Date().toISOString(),
            }).eq('id', fatura.id);

            await logAction(user?.id || 'SYSTEM', 'SKIP_SEND_BOLETO', {
                fatura_id: fatura.id,
                motivo: skipSendReason.trim(),
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'pular_envio_boleto', origem: 'acao' });

            await iniciarCobrancaCliente(sendNomeCliente, fatura);

            addToast('Cobrança marcada como enviada sem disparo do webhook.', 'success');
            setShowSkipSendScreen(false);
            setSkipSendReason('');
            setSendConfirmation(false);
            onUpdate();
            onClose();
        } catch (error: any) {
            addToast(error.message || 'Erro ao pular envio.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePreSendSkipToClient = async () => {
        if (!fatura) return;
        faturaDoEnvioRef.current = fatura;
        setSendNomeCliente(fatura.nome_cliente_boleto || fatura.cliente_nome || '');
        setSendTelefoneCliente('');
        setSendTelefoneCliente2('');
        setSendEmailCliente('');
        setSendMesReferencia(fatura.mes_referencia || '');
        setSendSkipConfirmation(true);

        // Fetch client details from `unidades_consumidoras` table
        if (fatura.uc_codigo || fatura.cliente_cpf_cnpj) {
            try {
                const contato = await buscarContatoUcParaCobranca(fatura.uc_codigo, fatura.cliente_cpf_cnpj);
                if (faturaDoEnvioRef.current?.id !== fatura.id) return; // já é outra fatura: não preenche com o contato desta
                if (contato) {
                    if (contato.whatsapp_cliente) setSendTelefoneCliente(contato.whatsapp_cliente);
                    if (contato.whatsapp_cliente_2) setSendTelefoneCliente2(contato.whatsapp_cliente_2);
                    if (contato.email_cliente) setSendEmailCliente(contato.email_cliente);
                    setAdesaoEnvio(fatura.empresa_parceira_id ? null : adesaoNoEnvio(contato));
                } else {
                    addToast('Não encontrei o WhatsApp/e-mail cadastrado desta UC automaticamente. Confira e preencha antes de enviar.', 'info');
                }
            } catch (err) {
                console.log('Error fetching client details from unidades_consumidoras:', err);
            }
        }
    };

    const handleConfirmSendSkipToClient = async () => {
        const fatura = faturaDoEnvioRef.current ?? faturaAberta;
        if (!fatura) return;

        // Mesma regra da tela de cobrança: WhatsApp OU e-mail, não os dois
        // obrigatoriamente. O e-mail só conta quando a UC está marcada com
        // receber_cobranca_email — é a flag que a edge function relê no servidor.
        const cleanPhoneCheck = sendTelefoneCliente.replace(/\D/g, '');
        const telefoneValido = cleanPhoneCheck.length >= TELEFONE_MIN_DIGITOS;
        const emailValido = emailServeComoCanal(receberEmail, sendEmailCliente);

        if (cleanPhoneCheck.length > 0 && !telefoneValido) {
            addToast('O Telefone (WhatsApp) está incompleto. Complete o número ou apague o campo.', 'error');
            return;
        }

        if (!telefoneValido && !emailValido) {
            addToast(
                receberEmail
                    ? 'Escolha por onde enviar: preencha o Telefone (WhatsApp) ou o e-mail do cliente.'
                    : 'O campo Telefone (WhatsApp) é obrigatório para enviar a comunicação.',
                'error',
            );
            return;
        }

        const segundoTelefoneCheck = sendTelefoneCliente2.replace(/\D/g, '');
        if (segundoTelefoneCheck && segundoTelefoneCheck.length < TELEFONE_MIN_DIGITOS) {
            addToast('O segundo WhatsApp está incompleto. Complete o número ou apague o campo.', 'error');
            return;
        }

        setLoading(true);

        try {
            const telefonesEnvio = montarTelefonesEnvio(sendTelefoneCliente, sendTelefoneCliente2);
            const enviarPorWhatsapp = telefonesEnvio.length > 0;

            const { data: webhookData } = await supabase
                .from('system_settings')
                .select('value')
                .eq('key', 'fatura_skip_send_webhook_url')
                .single();

            const webhookUrl = webhookData?.value;

            if (enviarPorWhatsapp && !webhookUrl) {
                addToast('Webhook de comunicação não configurado.', 'error');
                return;
            }

            const payload = {
                fatura_id: fatura.id,
                uc_codigo: fatura.uc_codigo,
                nome_cliente_boleto: sendNomeCliente,
                telefone_cliente: telefonesEnvio[0],
                telefones_cliente: telefonesEnvio,
                email_cliente: sendEmailCliente.trim() || null,
                mes_referencia: sendMesReferencia,
                motivo_pular_calculo: fatura.motivo_pular_calculo,
                pular_calculo_por_nome: fatura.pular_calculo_por_nome,
                pdf_fatura: fatura.pdf_fatura
            };

            if (enviarPorWhatsapp) {
                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao acionar webhook (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }
            }

            await supabase.from('faturas').update({
                status_fatura: 'Cobrança enviada',
                enviado_por_nome: profile?.full_name || user?.email || 'Sistema',
                enviado_em: new Date().toISOString(),
            }).eq('id', fatura.id);

            await logAction(user?.id || 'SYSTEM', 'SEND_SKIP_COM', { fatura_id: fatura.id, payload });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'enviar_skip_com', origem: 'acao' });

            // Fatura de acordo fica de fora — ver "Persistência na tabela
            // unidades_consumidoras" em handleConfirmSendToClient. Sem a permissão
            // "Editar dados cadastrais" também não grava (#6239).
            if (fatura.uc_codigo && !fatura.parcelamento_origem_id && podeEditarCadastro(profile)) {
                try {
                    const cleanCpfCnpj = (fatura.cliente_cpf_cnpj || '').replace(/\D/g, '');
                    const { data: ucList } = await supabase
                        .from('unidades_consumidoras')
                        .select('id, numero_uc, outro_numero_uc')
                        .or(`numero_uc.eq.${fatura.uc_codigo},outro_numero_uc.eq.${fatura.uc_codigo}`);

                    const findBestUC = (list: any[], code: string) => {
                        if (!list || list.length === 0) return null;
                        const primaryMatch = list.find(uc => uc.numero_uc === code);
                        if (primaryMatch) return primaryMatch;
                        const secondaryMatch = list.find(uc => uc.outro_numero_uc === code);
                        if (secondaryMatch) return secondaryMatch;
                        return list[0];
                    };

                    const existingUc = findBestUC(ucList || [], fatura.uc_codigo);

                    const ucData: any = {
                        numero_uc: fatura.uc_codigo,
                        nome_cliente: sendNomeCliente,
                        cpf_cnpj_cliente: cleanCpfCnpj,
                        // outro_email fica de fora de propósito: o campo saiu da tela,
                        // então gravar aqui apagaria o segundo e-mail já cadastrado na UC.
                        email_cliente: sendEmailCliente.trim() || null
                    };

                    // Mesmo cuidado da tela de cobrança: sem telefone na tela,
                    // gravar o campo apagaria o WhatsApp já cadastrado na UC.
                    if (enviarPorWhatsapp) {
                        ucData.whatsapp_cliente = sendTelefoneCliente;
                        ucData.whatsapp_cliente_2 = sendTelefoneCliente2.trim() || null;
                    }

                    if (existingUc) {
                        await supabase.from('unidades_consumidoras').update(ucData).eq('id', existingUc.id);
                    } else {
                        console.warn('UC não encontrada para atualizar contatos. Pulando criação automática.');
                    }
                } catch (err) {}
            }

            await iniciarCobrancaCliente(sendNomeCliente, fatura);

            if (enviarPorWhatsapp) {
                addToast('Comunicação enviada para o cliente com sucesso!', 'success');
            } else {
                addToast('Sem WhatsApp cadastrado — a comunicação vai apenas por e-mail.', 'info');
            }

            // Canal de e-mail da comunicação: mesma function da cobrança, em
            // modo 'comunicado' — sem valor, vencimento, PIX ou linha digitável,
            // que não existem numa fatura que pulou o cálculo. Best-effort igual
            // ao envio de cobrança: nunca lança, nunca desfaz o que já foi feito.
            if (receberEmail) {
                try {
                    const destinatariosComunicado = [sendEmailCliente.trim()].filter(Boolean);
                    const resultado = await enviarCobrancaEmail(fatura.id, {
                        destinatarios: destinatariosComunicado,
                        modo: 'comunicado',
                    });
                    if (resultado.ok) {
                        logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'enviar_comunicado_email', origem: 'acao' });
                        await logAction(user?.id || 'SYSTEM', 'SEND_COMUNICADO_EMAIL', {
                            fatura_id: fatura.id,
                            destinatarios: resultado.destinatarios || [],
                            anexos: resultado.anexos || [],
                            warnings: resultado.warnings || [],
                        });
                        addToast(`Comunicação enviada por e-mail para ${(resultado.destinatarios || []).join(', ')}.`, 'success');
                    } else if (resultado.motivo === 'opt_out') {
                        addToast('E-mail não enviado: esta UC não está marcada para receber cobrança por e-mail.', 'info');
                    } else if (resultado.motivo === 'ja_enviado_recentemente') {
                        addToast('O e-mail desta fatura já havia sido enviado há poucos minutos — nenhum novo e-mail foi disparado.', 'info');
                    } else {
                        await logAction(user?.id || 'SYSTEM', 'SEND_COMUNICADO_EMAIL_FALHOU', {
                            fatura_id: fatura.id,
                            motivo: resultado.motivo,
                            mensagem: resultado.mensagem,
                        });
                        addToast(
                            enviarPorWhatsapp
                                ? `A comunicação foi enviada no WhatsApp, mas o e-mail falhou: ${resultado.mensagem}.`
                                : `O e-mail falhou e não havia WhatsApp: ${resultado.mensagem}. O cliente NÃO recebeu a comunicação.`,
                            'warning',
                            12000,
                        );
                    }
                } catch (emailErr) {
                    console.error('Unexpected error sending comunicado e-mail:', emailErr);
                }
            }

            setSendSkipConfirmation(false);
            onUpdate();
            onClose();

        } catch (error: any) {
            console.error('Error sending skip communication:', error);
            addToast(error.message || 'Erro ao enviar comunicação.', 'error');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Baixa o boleto no VigorHub: status, motivo, histórico e limpeza dos campos do
     * boleto. Usado pelo caminho Sicoob/n8n depois do cancelamento no banco e,
     * sozinho, quando não existe cobrança registrada para cancelar lá.
     * (O caminho Asaas não passa por aqui: a edge function já atualiza a fatura.)
     */
    const cancelarSomenteNoVigorHub = async () => {
        if (!fatura) return;
        const autor = profile?.full_name || user?.email || 'Sistema';
        const agora = new Date().toISOString();
        const newCancellation = {
                        // Qual boleto foi baixado. Sem isto o histórico registra quem, quando e por
            // quê, mas não O QUE — e como `num_cobranca_boleto` é zerado no mesmo update,
            // o número some da fatura por completo. Auditar boleto órfão virava inferência
            // por subtração entre `historico_emissoes` e a contagem de cancelamentos (foi
            // como o caso 11172 teve de ser levantado em 21/09/2026).
            num_cobranca_boleto: fatura.num_cobranca_boleto ?? null,
            cancelado_por_nome: autor,
            cancelado_em: agora,
            motivo_cancelamento_boleto: cancelReason.trim(),
            motivo_codigo: motivoCancel.codigo || null,
        };
        const updatedHistory = Array.isArray(fatura.historico_cancelamentos)
            ? [...fatura.historico_cancelamentos, newCancellation]
            : [newCancellation];

        const { error } = await supabase.from('faturas').update({
            status_boleto_vigor: 'Cancelado',
            status_fatura: 'Fatura validada',
            motivo_cancelamento_boleto: cancelReason.trim(),
            cancelado_por_nome: autor,
            cancelado_em: agora,
            historico_cancelamentos: updatedHistory,
            url_boleto: null,
            codigo_pix_boleto: null,
            linha_digitavel_boleto: null,
            num_cobranca_boleto: null,
            valor_consumo_scee_boleto: null,
            valor_adc_bandeira_boleto: null,
            valor_parc_injet_s_desc_boleto: null,
            valor_restituicao_imposto_boleto: null,
            valor_energia_nao_isenta_boleto: null,
            valor_boleto_vigor: null
        }).eq('id', fatura.id);

        // supabase-js não lança: sem checar, uma falha aqui deixava o boleto baixado no
        // Sicoob e a fatura viva na tela, com o operador vendo "cancelado".
        if (error) {
            throw new Error(
                'O boleto foi baixado, mas a fatura não pôde ser atualizada: ' + error.message
                + '. Recarregue a página e cancele de novo — a baixa já feita não se repete.'
            );
        }
    };

    const handleCancelBoleto = async () => {
        if (!fatura) return;

        const faltaMotivo = faltaNoMotivo(motivoCancel.codigo, motivoCancel.detalhe);
        if (faltaMotivo) {
            addToast(faltaMotivo, 'error');
            return;
        }

        setLoading(true);
        try {
            const gateway = await resolveBancoGateway(fatura.empresa_parceira_id);

            // Boleto que nunca foi registrado no gateway não tem o que baixar. Chamar o
            // Sicoob assim manda `nossoNumero: null`, o n8n monta `/boletos/null/baixar`
            // e o gateway devolve 400 "The request URI was invalid" — erro de URI, não de
            // negócio, que só deixava o operador sem conseguir cancelar (UC 346058301294,
            // fatura 9404 de JUL/2026). O cancelamento se resolve inteiro aqui.
            const jaEstaPago = fatura.status_boleto_vigor?.toLowerCase() === 'pago';
            // Teste do caminho Sicoob é simulado (emitir-boleto-teste): não existe no banco.
            // Não pode ir para o n8n, cujo fluxo de baixa aponta fixo para o Sicoob real.
            // O cancelamento local não mexe em `ambiente`, então a fatura segue de teste.
            if (fatura.ambiente === 'sandbox' && gateway !== 'asaas') {
                await cancelarSomenteNoVigorHub();
                addToast('Boleto de teste cancelado. Ele não existia no banco, então nada foi baixado lá.', 'success');
            } else if (!boletoExisteNoGateway(fatura) || jaEstaPago) {
                await cancelarSomenteNoVigorHub();
                addToast(jaEstaPago ? 'Pagamento desvinculado localmente com sucesso!' : MOTIVO_CANCELAMENTO_LOCAL, 'success', 12000);
            } else if (gateway === 'asaas') {
                // A própria edge function já atualiza a fatura (zera campos, status, histórico).
                await cancelarCobrancaAsaas(fatura.id, cancelReason.trim());
                addToast('Boleto cancelado com sucesso!', 'success');
            } else {
                // Fetch the cancellation webhook
                const { data: webhookData } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_cancellation_webhook_url')
                    .single();

                const webhookUrl = webhookData?.value;

                if (!webhookUrl) {
                    addToast('Webhook de cancelamento não configurado.', 'error');
                    return;
                }

                // Resolve a conta Sicoob (Associação vs Gestão) para o n8n rotear a baixa corretamente
                const contaSicoob = await resolveContaSicoob(fatura);
                const emitirGestao = contaSicoob === 'gestao';

                // Send webhook request
                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: fatura.id,
                        nossoNumero: fatura.num_cobranca_boleto,
                        motivoCancelamento: cancelReason.trim(),
                        ...fatura,
                        emitir_em_vigor_gestao: emitirGestao,
                        conta_sicoob: contaSicoob
                    })
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao acionar webhook (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }

                try {
                    const responseData = await response.json();

                    if (responseData?.status === 'erro') {
                        throw new Error(responseData.mensagem || 'Erro retornado pelo servidor.');
                    }

                    if (responseData?.mensagem) {
                        addToast(responseData.mensagem, 'success');
                    } else {
                        addToast('Solicitação de cancelamento processada!', 'success');
                    }
                } catch (e: any) {
                    if (e.message) throw e;
                    // If the response is not JSON or other error
                    addToast('Solicitação de cancelamento processada!', 'success');
                }

                await cancelarSomenteNoVigorHub();
            }

            await logAction(user?.id || 'SYSTEM', 'CANCEL_BOLETO_WEBHOOK', { fatura_id: fatura.id, motivo: cancelReason.trim(), motivo_codigo: motivoCancel.codigo || null });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'cancelar_boleto', origem: 'acao' });

            setCancelConfirmation(false);
            setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error.message || 'Erro ao cancelar boleto.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const openProrrogar = () => {
        if (!fatura) return;
        const atual = fatura.data_limite_pagamento_boleto || addDaysISO(fatura.vencimento_boleto, 30) || '';
        setNovaDataLimite(atual);
        setProrrogarConfirmation(true);
    };

    const handleProrrogarBoleto = async () => {
        if (!fatura) return;

        if (!fatura.num_cobranca_boleto) {
            addToast('Boleto sem nº de cobrança (nossoNumero). Não é possível prorrogar.', 'error');
            return;
        }
        if (!novaDataLimite) {
            addToast('Informe a nova data limite de pagamento.', 'error');
            return;
        }
        if (fatura.vencimento_boleto && novaDataLimite <= fatura.vencimento_boleto) {
            addToast('A nova data limite deve ser posterior ao vencimento do boleto.', 'error');
            return;
        }

        setLoading(true);
        try {
            const gateway = await resolveBancoGateway(fatura.empresa_parceira_id);

            if (gateway === 'asaas') {
                // A própria edge function já atualiza data_limite_pagamento_boleto na fatura.
                await prorrogarCobrancaAsaas(fatura.id, novaDataLimite);
                addToast('Prorrogação realizada com sucesso!', 'success');
            } else {
                const { data: webhookData } = await supabase
                    .from('system_settings')
                    .select('value')
                    .eq('key', 'boleto_prorrogacao_webhook_url')
                    .single();

                const webhookUrl = webhookData?.value;

                if (!webhookUrl) {
                    addToast('Webhook de prorrogação não configurado.', 'error');
                    setLoading(false);
                    return;
                }

                // Resolve a conta Sicoob (Associação vs Gestão) para o n8n rotear corretamente
                const contaSicoob = await resolveContaSicoob(fatura);
                const emitirGestao = contaSicoob === 'gestao';

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: fatura.id,
                        nossoNumero: fatura.num_cobranca_boleto,
                        nova_data_limite_pagamento: novaDataLimite,
                        emitir_em_vigor_gestao: emitirGestao,
                        conta_sicoob: contaSicoob,
                    }),
                });

                if (!response.ok) {
                    let errorMsg = `Falha ao acionar webhook (Status: ${response.status})`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData?.mensagem || errorData?.message || errorData?.error?.message || errorMsg;
                    } catch (e) { }
                    throw new Error(errorMsg);
                }

                try {
                    const responseData = await response.json();
                    if (responseData?.status === 'erro') {
                        throw new Error(responseData.mensagem || 'Erro retornado pelo servidor.');
                    }
                    addToast(responseData?.mensagem || 'Prorrogação realizada com sucesso!', 'success');
                } catch (e: any) {
                    if (e.message) throw e;
                    addToast('Prorrogação realizada com sucesso!', 'success');
                }

                await supabase.from('faturas').update({
                    data_limite_pagamento_boleto: novaDataLimite,
                }).eq('id', fatura.id);
            }

            await logAction(user?.id || 'SYSTEM', 'PRORROGAR_BOLETO_WEBHOOK', {
                fatura_id: fatura.id,
                nova_data_limite_pagamento: novaDataLimite,
            });
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'prorrogar_boleto', origem: 'acao' });

            setProrrogarConfirmation(false);
            onUpdate();
            onClose();
        } catch (error: any) {
            console.error(error);
            addToast(error.message || 'Erro ao prorrogar boleto.', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !fatura) return null;

    return (
        createPortal(
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4"
                style={{ zIndex: 9999 }}
                onMouseDown={e => { backdropMouseDownRef.current = e.target === e.currentTarget; }}
                onClick={() => { if (backdropMouseDownRef.current) onClose(); backdropMouseDownRef.current = false; }}
            >

                {/* Cancel Confirmation */}
                {cancelConfirmation && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                        <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertTriangle size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Cancelar Boleto?</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-6">
                                {fatura?.status_boleto_vigor?.toLowerCase() === 'pago' ? (
                                    <span className="text-amber-600 dark:text-amber-400 font-medium">Atenção: Este boleto consta como PAGO no sistema. O cancelamento será realizado apenas localmente no VigorHub (desvinculando o pagamento atual) para permitir uma nova emissão.</span>
                                ) : (
                                    <>Uma solicitação de cancelamento será enviada. O status da fatura será atualizado para "Cancelado" se a operação for bem-sucedida no gateway.</>
                                )}
                            </p>
                            
                            <CampoMotivoCancelamento valor={motivoCancel} aoMudar={setMotivoCancel} />

                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => {
                                        setCancelConfirmation(false);
                                        setMotivoCancel(SEM_MOTIVO_CANCELAMENTO);
                                    }}
                                    className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Voltar
                                </button>
                                <button
                                    onClick={handleCancelBoleto}
                                    disabled={loading}
                                    className="px-6 py-2.5 rounded-xl border-2 border-red-500 text-red-600 font-bold hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors disabled:opacity-50"
                                >
                                    {loading ? 'Cancelando...' : 'Sim, cancelar'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Prorrogar Confirmation */}
                {prorrogarConfirmation && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4">
                        <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-md p-6 text-center animate-fade-in border dark:border-gray-700">
                            <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 text-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CalendarDays size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Prorrogar data limite de pagamento</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-6">
                                Estende o prazo de pagamento do boleto <strong>sem reemitir</strong> (sem nova tarifa de emissão). O vencimento e os encargos (multa/juros) seguem a partir do vencimento original. Só funciona enquanto o boleto está "Em aberto" no Sicoob.
                            </p>

                            <div className="mb-6 text-left">
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    Nova data limite de pagamento <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={novaDataLimite}
                                    min={fatura.data_limite_pagamento_boleto || fatura.vencimento_boleto || undefined}
                                    onChange={(e) => setNovaDataLimite(e.target.value)}
                                    className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                />
                                {fatura.data_limite_pagamento_boleto && (
                                    <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">Limite atual: {fatura.data_limite_pagamento_boleto.split('-').reverse().join('/')}</p>
                                )}
                            </div>

                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setProrrogarConfirmation(false)}
                                    className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Voltar
                                </button>
                                <button
                                    onClick={handleProrrogarBoleto}
                                    disabled={loading}
                                    className="px-6 py-2.5 rounded-xl border-2 border-amber-500 text-amber-600 font-bold hover:bg-amber-50 dark:hover:bg-amber-900/10 transition-colors disabled:opacity-50"
                                >
                                    {loading ? 'Prorrogando...' : 'Confirmar prorrogação'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}



                {/* Send Confirmation */}
                {sendConfirmation && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4 overflow-y-auto">
                        <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-2xl p-6 animate-fade-in border dark:border-gray-700 m-auto mt-10 mb-10">
                            {showSkipSendScreen ? (
                                <>
                                    <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900/30 text-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <SkipForward size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Pular Envio</h3>
                                    <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">
                                        A cobrança será movida para "Cobrança enviada" sem acionar o webhook de envio. Informe o motivo abaixo.
                                    </p>
                                    <div className="mb-6">
                                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                            Motivo <span className="text-red-500">*</span>
                                        </label>
                                        <textarea
                                            value={skipSendReason}
                                            onChange={(e) => setSkipSendReason(e.target.value)}
                                            placeholder="Descreva o motivo pelo qual o envio está sendo pulado..."
                                            className="w-full px-3 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50 resize-none h-28"
                                        />
                                    </div>
                                    <div className="flex gap-3 justify-center">
                                        <button
                                            onClick={() => { setShowSkipSendScreen(false); setSkipSendReason(''); }}
                                            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Voltar
                                        </button>
                                        <button
                                            onClick={handleConfirmSkipSend}
                                            disabled={loading || !skipSendReason.trim()}
                                            className="px-6 py-2.5 rounded-xl bg-orange-500 text-white font-bold hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {loading ? 'Salvando...' : <><SkipForward size={18} /> Pular Envio</>}
                                        </button>
                                    </div>
                                </>
                            ) : showPreview ? (
                                <>
                                    <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Eye size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Prévia do envio</h3>
                                    <p className="text-gray-500 dark:text-gray-400 mb-6 text-center text-sm">
                                        Estes são os dados exatos que vão para o cliente, já formatados como ele vai ver.
                                    </p>

                                    <PainelChecagens bloqueios={bloqueiosEnvio} alertas={alertasEnvio} />
                                    <SeloAdesaoEnvio adesao={adesaoEnvio} nomeCliente={sendNomeCliente} onCopiado={() => addToast('Convite de adesão copiado. Cole na conversa do cliente.', 'success')} />

                                    {/* Destinatários — já normalizados (55 na frente, sem repetidos),
                                        que é como o UaisyChat recebe. Mostrar o número final evita a
                                        dúvida de "será que foi pro WhatsApp certo?" depois do envio. */}
                                    <div className="mb-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
                                        <span className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                            {previaEnvio.destinatarios.length === 0
                                                ? 'Somente e-mail'
                                                : previaEnvio.destinatarios.length > 1 ? 'WhatsApp — 2 destinatários' : 'WhatsApp'}
                                        </span>
                                        <div className="mt-1 flex flex-wrap gap-2">
                                            {previaEnvio.destinatarios.map(d => (
                                                <span key={d} className="px-2.5 py-1 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 text-sm font-mono">
                                                    +{d}
                                                </span>
                                            ))}
                                        </div>
                                        {previaEnvio.emails.length > 0 ? (
                                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                {previaEnvio.destinatarios.length === 0 ? 'Vai por e-mail para ' : 'E também por e-mail para '}
                                                <span className="font-mono">{previaEnvio.emails.join(', ')}</span>
                                                {previaEnvio.destinatarios.length === 0
                                                    ? ' — nenhuma mensagem de WhatsApp será disparada.'
                                                    : null}
                                            </p>
                                        ) : null}
                                    </div>

                                    {/* Bolha estilo WhatsApp: o conteúdo é o dado real que o n8n manda
                                        ao UaisyChat. O texto final da mensagem vem do template WABA
                                        aprovado na Meta (fica no UaisyChat, não neste código) — por isso
                                        a prévia mostra os CAMPOS, que é o que muda a cada cobrança e o
                                        que de fato quebrou no caso Portal dos Buritis. */}
                                    <div className="mb-4 rounded-xl border border-emerald-200 dark:border-emerald-900/50 bg-[#e7f7e0] dark:bg-emerald-950/20 p-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <LinhaPrevia rotulo="Cliente" valor={previaEnvio.nome} />
                                            <LinhaPrevia rotulo="Unidade consumidora" valor={previaEnvio.uc} ausente="— sem UC —" />
                                            <LinhaPrevia rotulo="Mês de referência" valor={previaEnvio.mesReferencia} />
                                            <LinhaPrevia rotulo="Vencimento" valor={previaEnvio.vencimento} />
                                            <div className="col-span-2">
                                                <LinhaPrevia rotulo="Valor" valor={previaEnvio.valor ? `R$ ${previaEnvio.valor}` : ''} />
                                            </div>
                                            <div className="col-span-2">
                                                <LinhaPrevia
                                                    rotulo="PIX copia e cola"
                                                    valor={previaEnvio.codigoPix}
                                                    mono
                                                    ausente="— SEM PIX —"
                                                />
                                            </div>
                                            <div className="col-span-2">
                                                <LinhaPrevia
                                                    rotulo="Linha digitável (código de barras)"
                                                    valor={previaEnvio.linhaDigitavel}
                                                    mono
                                                    ausente="— SEM CÓDIGO DE BARRAS —"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-6 flex flex-wrap gap-2 text-xs">
                                        <span className={`px-2.5 py-1 rounded-lg ${fatura?.url_boleto ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'}`}>
                                            {fatura?.url_boleto ? '✓ Boleto (PDF) anexado' : '⚠ Sem PDF do boleto'}
                                        </span>
                                        <span className={`px-2.5 py-1 rounded-lg ${fatura?.pdf_fatura ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'}`}>
                                            {fatura?.pdf_fatura ? '✓ Fatura da distribuidora anexada' : '⚠ Sem fatura da distribuidora'}
                                        </span>
                                    </div>

                                    <div className="flex gap-3 justify-center items-start">
                                        <button
                                            onClick={() => setShowPreview(false)}
                                            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Voltar e editar
                                        </button>
                                        <button
                                            onClick={handleConfirmSendToClient}
                                            disabled={loading || envioBloqueado}
                                            className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            {loading ? 'Enviando...' : <><Send size={18} /> Confirmar e Enviar</>}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Send size={32} />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Confirmar Envio</h3>
                                    <p className="text-gray-500 dark:text-gray-400 mb-6 text-center">
                                        Verifique e se necessário edite os dados abaixo. O próximo passo mostra a prévia do que o cliente vai receber.
                                    </p>

                                    <PainelChecagens bloqueios={bloqueiosEnvio} alertas={alertasEnvio} />
                                    <SeloAdesaoEnvio adesao={adesaoEnvio} nomeCliente={sendNomeCliente} onCopiado={() => addToast('Convite de adesão copiado. Cole na conversa do cliente.', 'success')} />

                                    {/* Aviso de envio adicional por e-mail — a UC está marcada para receber
                                        cobrança também por e-mail (receber_cobranca_email). É só um aviso:
                                        não desabilita o botão de envio, e a falha do e-mail nunca reverte
                                        o envio principal por WhatsApp. */}
                                    {receberEmail && (
                                        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 rounded-r-xl flex items-start gap-3">
                                            <div className="p-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-lg shrink-0">
                                                <Mail size={18} />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">
                                                    Esta cobrança também será enviada por e-mail
                                                </h4>
                                                {emailsDestino.length > 0 ? (
                                                    <p className="text-xs text-amber-700/90 dark:text-amber-300/90 mt-1 font-mono break-all">
                                                        Para: {emailsDestino.join(', ')}
                                                    </p>
                                                ) : (
                                                    <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                                                        Nenhum e-mail informado — preencha ao menos um campo abaixo.
                                                    </p>
                                                )}
                                                <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1">
                                                    Remetente: contato@vigorenergy.com.br
                                                </p>
                                                <p className="text-xs text-amber-700/80 dark:text-amber-300/80 mt-1">
                                                    Vão anexados a fatura da distribuidora e o boleto (PDF), além do PIX e da linha digitável no corpo da mensagem.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do Cliente</label>
                                            <input type="text" value={sendNomeCliente} onChange={(e) => setSendNomeCliente(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Telefone (WhatsApp){' '}
                                                {receberEmail
                                                    ? <span className="text-gray-400 font-normal">(ou e-mail)</span>
                                                    : <span className="text-red-500">*</span>}
                                            </label>
                                            <input
                                                type="text"
                                                value={sendTelefoneCliente}
                                                onChange={(e) => setSendTelefoneCliente(formatTelefone(e.target.value))}
                                                placeholder="(XX) 00000-0000"
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                                    campoTemBloqueio('telefone')
                                                        ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                        : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                                }`}
                                            />
                                            {campoTemBloqueio('telefone') ? (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {receberEmail
                                                        ? 'Preencha o WhatsApp ou o e-mail — pelo menos um dos dois.'
                                                        : 'Campo obrigatório para enviar a cobrança.'}
                                                </p>
                                            ) : null}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                2º WhatsApp <span className="text-gray-400 font-normal">(opcional)</span>
                                            </label>
                                            <input
                                                type="text"
                                                value={sendTelefoneCliente2}
                                                onChange={(e) => setSendTelefoneCliente2(formatTelefone(e.target.value))}
                                                placeholder="(XX) 00000-0000"
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                                    sendTelefoneCliente2.replace(/\D/g, '').length > 0 && sendTelefoneCliente2.replace(/\D/g, '').length < TELEFONE_MIN_DIGITOS
                                                        ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                        : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                                }`}
                                            />
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Preenchido, o boleto vai por WhatsApp para os dois números.
                                            </p>
                                        </div>
                                        {receberEmail ? (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                    E-mail do Cliente{' '}
                                                    {temWhatsappParaEnvio
                                                        ? <span className="text-gray-400 font-normal">(opcional)</span>
                                                        : <span className="text-red-500">*</span>}
                                                </label>
                                                <input
                                                    type="email"
                                                    value={sendEmailCliente}
                                                    onChange={(e) => setSendEmailCliente(e.target.value)}
                                                    placeholder="email@exemplo.com"
                                                    className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                                        campoTemBloqueio('email')
                                                            ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                            : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                                    }`}
                                                />
                                                {!temWhatsappParaEnvio ? (
                                                    <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                                        Sem WhatsApp preenchido, o e-mail é o único canal desta cobrança.
                                                    </p>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mês de Referência</label>
                                            <input type="text" value={sendMesReferencia} onChange={(e) => setSendMesReferencia(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valor do Boleto (R$)</label>
                                            <input
                                                type="number"
                                                step="0.01"
                                                value={sendValorBoleto}
                                                onChange={(e) => setSendValorBoleto(e.target.value)}
                                                onBlur={(e) => {
                                                    if (e.target.value) setSendValorBoleto(Number(e.target.value).toFixed(2));
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vencimento do Boleto</label>
                                            <input type="date" value={sendVencimentoBoleto} onChange={(e) => setSendVencimentoBoleto(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50" />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Código PIX {sendCodigoPix.trim() ? null : <span className="text-gray-400 font-normal">(vazio)</span>}
                                            </label>
                                            <input
                                                type="text"
                                                value={sendCodigoPix}
                                                onChange={(e) => setSendCodigoPix(e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm outline-none focus:ring-2 ${
                                                    campoTemBloqueio('codigoPix') || campoTemBloqueio('formaPagamento')
                                                        ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                        : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                                }`}
                                            />
                                        </div>
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                Linha Digitável (código de barras) {sendLinhaDigitavel.trim() ? null : <span className="text-gray-400 font-normal">(vazio)</span>}
                                            </label>
                                            <input
                                                type="text"
                                                value={sendLinhaDigitavel}
                                                onChange={(e) => setSendLinhaDigitavel(e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm outline-none focus:ring-2 ${
                                                    campoTemBloqueio('linhaDigitavel') || campoTemBloqueio('formaPagamento')
                                                        ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                        : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                                }`}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex gap-3 justify-center items-start">
                                        <button
                                            onClick={() => { setSendConfirmation(false); setShowSkipSendScreen(false); setSkipSendReason(''); setShowPreview(false); }}
                                            className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            Voltar
                                        </button>
                                        <div className="flex flex-col items-center gap-2">
                                            <button
                                                onClick={() => setShowPreview(true)}
                                                disabled={loading || envioBloqueado}
                                                title={envioBloqueado ? bloqueiosEnvio[0].mensagem : undefined}
                                                className="px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                <Eye size={18} /> Ver prévia do envio
                                            </button>
                                            {envioBloqueado ? (
                                                <p className="text-xs text-red-500 max-w-xs text-center">
                                                    Resolva a{bloqueiosEnvio.length > 1 ? 's' : ''} pendência{bloqueiosEnvio.length > 1 ? 's' : ''} acima para liberar o envio.
                                                </p>
                                            ) : null}
                                            <button
                                                onClick={() => setShowSkipSendScreen(true)}
                                                className="text-sm text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 hover:underline underline-offset-2 transition-colors"
                                            >
                                                Pular envio
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Send Skip Confirmation */}
                {sendSkipConfirmation && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4 overflow-y-auto">
                        <div className="bg-white dark:bg-gray-800/90 rounded-2xl shadow-2xl w-full max-w-2xl p-6 animate-fade-in border dark:border-gray-700 m-auto mt-10 mb-10">
                            <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Send size={32} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 text-center">Confirmar Envio de Comunicação</h3>
                            <p className="text-gray-500 dark:text-gray-400 mb-4 text-center">
                                Verifique os dados abaixo antes de enviar a comunicação informativa ao cliente.
                            </p>
                            
                            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900/30 p-3 rounded-xl mb-6 flex gap-3 items-center">
                                <AlertTriangle className="text-amber-500 shrink-0" size={18} />
                                <p className="text-xs text-amber-800 dark:text-amber-400 font-medium">
                                    Esta ação <strong>não envia boleto</strong>, apenas notifica o cliente sobre a fatura deste mês.
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome do Cliente</label>
                                    <input type="text" value={sendNomeCliente} onChange={(e) => setSendNomeCliente(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Telefone (WhatsApp){' '}
                                        {receberEmail
                                            ? <span className="text-gray-400 font-normal">(ou e-mail)</span>
                                            : <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type="text"
                                        value={sendTelefoneCliente}
                                        onChange={(e) => setSendTelefoneCliente(formatTelefone(e.target.value))}
                                        placeholder="(XX) 00000-0000"
                                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                            comunicacaoSemCanal || (sendTelefoneCliente.replace(/\D/g, '').length > 0 && !temWhatsappParaEnvio)
                                                ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                        }`}
                                    />
                                    {comunicacaoSemCanal && (
                                        <p className="mt-1 text-xs text-red-500">
                                            {receberEmail
                                                ? 'Preencha o WhatsApp ou o e-mail — pelo menos um dos dois.'
                                                : 'Campo obrigatório para enviar a comunicação.'}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        2º WhatsApp <span className="text-gray-400 font-normal">(opcional)</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={sendTelefoneCliente2}
                                        onChange={(e) => setSendTelefoneCliente2(formatTelefone(e.target.value))}
                                        placeholder="(XX) 00000-0000"
                                        className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                            sendTelefoneCliente2.replace(/\D/g, '').length > 0 && sendTelefoneCliente2.replace(/\D/g, '').length < TELEFONE_MIN_DIGITOS
                                                ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                        }`}
                                    />
                                </div>
                                {receberEmail ? (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            E-mail do Cliente{' '}
                                            {temWhatsappParaEnvio
                                                ? <span className="text-gray-400 font-normal">(opcional)</span>
                                                : <span className="text-red-500">*</span>}
                                        </label>
                                        <input
                                            type="email"
                                            value={sendEmailCliente}
                                            onChange={(e) => setSendEmailCliente(e.target.value)}
                                            placeholder="email@exemplo.com"
                                            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 ${
                                                comunicacaoSemCanal
                                                    ? 'border-red-400 dark:border-red-500 focus:ring-red-300'
                                                    : 'border-gray-300 dark:border-gray-600 focus:ring-primary/50'
                                            }`}
                                        />
                                        {!temWhatsappParaEnvio ? (
                                            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                                                Sem WhatsApp preenchido, o e-mail é o único canal desta comunicação.
                                            </p>
                                        ) : null}
                                    </div>
                                ) : null}
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Mês de Referência</label>
                                    <input type="text" value={sendMesReferencia} onChange={(e) => setSendMesReferencia(e.target.value)} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/50" />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 justify-center">
                                <button
                                    onClick={() => setSendSkipConfirmation(false)}
                                    className="px-6 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Voltar
                                </button>
                                <button
                                    onClick={handleConfirmSendSkipToClient}
                                    disabled={loading}
                                    className="px-6 py-2.5 rounded-xl bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                                >
                                    {loading ? 'Enviando...' : <><Send size={18} /> Confirmar e Enviar</>}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Modal */}
                <div
                    className={`bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border dark:border-gray-700 ${(cancelConfirmation || prorrogarConfirmation || sendConfirmation || sendSkipConfirmation) ? 'opacity-0 pointer-events-none' : ''}`}
                    onClick={e => e.stopPropagation()}
                >

                    {/* Header */}
                    <div className="p-8 border-b border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gray-50/50 dark:bg-gray-800/40 sticky top-0 z-10 shrink-0 backdrop-blur-md">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                                <CheckCircle className="text-green-500" size={28} />
                                Boleto Emitido
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-10">
                                {fatura.cliente_nome} • {fatura.uc_codigo} • {fatura.mes_referencia}
                            </p>
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                            {fatura.pdf_fatura && (
                                <button
                                    onClick={() => window.open(fatura.pdf_fatura, '_blank')}
                                    className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white bg-secondary hover:bg-secondary/80 rounded-lg transition-colors mr-2"
                                >
                                    <Eye size={16} />
                                    Fatura da Distribuidora
                                </button>
                            )}
                            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                                <X size={24} className="text-gray-400" />
                            </button>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="flex-1 bg-gray-50 dark:bg-white/5 overflow-y-auto border-y border-gray-100 dark:border-gray-700">
                        {/* Lembretes */}
                        <div className="p-8 pb-0 shrink-0">
                            <LembretesViewer uc_codigo={fatura.uc_codigo} mes_referencia={fatura.mes_referencia} />
                        </div>

                        {/* Email preference warning banner */}
                        {receberEmail && (
                            <div className="mx-8 mt-6 p-4 bg-amber-50 dark:bg-amber-950/20 border-l-4 border-amber-500 rounded-r-xl flex items-start gap-4 animate-fade-in transition-all">
                                <div className="p-2 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-lg shrink-0 animate-bounce">
                                    <Mail size={20} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">Aviso de Cobrança</h4>
                                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1 font-semibold">
                                        Atenção! É necessário enviar essa cobrança também via e-mail.
                                    </p>
                                </div>
                            </div>
                        )}
                        
                        {/* Banner de substituição de PDF */}
                        {canSubstitutePdf(fatura) && (
                            <div className={`p-4 border-b flex items-start gap-4 sticky top-0 z-10 backdrop-blur-md ${envioBloqueadoPorEspelho ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-900/30' : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-900/30'}`}>
                                <div className={`p-2 rounded-lg shrink-0 ${envioBloqueadoPorEspelho ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' : 'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'}`}>
                                    <AlertTriangle size={20} />
                                </div>
                                <div className="flex-1">
                                    {isPdfEspelho(fatura) && isFaturaZerada(fatura) ? (
                                        <>
                                            <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">Espelho de fatura zerada (R$ 0,00)</h4>
                                            <p className="text-xs text-amber-700/80 dark:text-amber-500/80 mt-1">
                                                A Equatorial <strong>não emite a fatura definitiva quando o valor é zero</strong> — o espelho é o único documento que existe. Como não há nada a cobrar, o envio da comunicação está liberado (o espelho não vai anexado).
                                            </p>
                                        </>
                                    ) : isPdfEspelho(fatura) && ucPermiteEspelho ? (
                                        <>
                                            <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">Espelho liberado para este cliente</h4>
                                            <p className="text-xs text-amber-700/80 dark:text-amber-500/80 mt-1">
                                                O PDF é o espelho e <strong>não tem PIX nem código de barras</strong>, mas o cadastro desta UC autoriza o envio assim{ucMotivoEspelho ? <> — <em>{ucMotivoEspelho}</em></> : null}. Se a fatura definitiva já existir, prefira substituir antes de enviar.
                                            </p>
                                        </>
                                    ) : isPdfEspelho(fatura) ? (
                                        <>
                                            <h4 className="text-sm font-bold text-red-800 dark:text-red-400">PDF anexado é o Espelho da Fatura</h4>
                                            <p className="text-xs text-red-700/80 dark:text-red-500/80 mt-1">
                                                Este PDF <strong>não contém PIX nem código de barras</strong> para o cliente pagar. Carregue a fatura definitiva da Equatorial para liberar o envio.
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">Substituição de PDF disponível</h4>
                                            <p className="text-xs text-amber-700/80 dark:text-amber-500/80 mt-1">
                                                Fatura de {fatura.mes_referencia} — você pode substituir o PDF por uma versão atualizada.
                                            </p>
                                        </>
                                    )}
                                </div>
                                <label className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-colors shrink-0 cursor-pointer ${replacingPdfLoading ? 'bg-gray-400 cursor-not-allowed' : envioBloqueadoPorEspelho ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'}`}>
                                    <Upload size={16} />
                                    {replacingPdfLoading ? 'Enviando...' : 'Substituir PDF'}
                                    <input
                                        type="file"
                                        accept="application/pdf"
                                        className="hidden"
                                        disabled={replacingPdfLoading}
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file) handleReplacePdf(file);
                                        }}
                                    />
                                </label>
                            </div>
                        )}

                        {/* Skip Calculation Warning Banner — só sem boleto: pulo desfeito que virou
                            boleto não é mais "envio informativo". */}
                        {fatura.motivo_pular_calculo && !fatura.url_boleto && (
                            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/30 flex items-start gap-4 sticky top-0 z-10 backdrop-blur-md">
                                <div className="p-2 bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 rounded-lg shrink-0">
                                    <AlertTriangle size={20} />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-amber-800 dark:text-amber-400">Ação: Envio de Comunicação (Sem Boleto)</h4>
                                    <p className="text-xs text-amber-700/80 dark:text-amber-500/80 mt-1">
                                        Esta fatura foi marcada para <strong>não gerar boleto</strong>. Ao prosseguir, você enviará apenas uma comunicação informativa ao cliente sobre o mês de referência.
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="p-8 min-h-[300px] flex items-center justify-center">
                        {fatura.url_boleto ? (
                            <div className="flex flex-col items-center justify-center text-center max-w-sm">
                                <div className="mb-6 flex items-center justify-center">
                                    <AnimatedBoletoIcon className="w-24 h-24" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                                    {semPdfUnificado ? 'Falta o PDF unificado' : 'Boleto pronto!'}
                                </h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-6 font-medium">
                                    {semPdfUnificado
                                        ? 'A cobrança existe no Asaas, mas o boleto unificado não foi gerado.'
                                        : 'O boleto foi gerado com sucesso e salvo de forma segura.'}
                                </p>

                                {/* Logo abaixo do título, e não junto dos botões: lá embaixo ficava
                                    fora da tela, atrás do histórico. */}
                                {semPdfUnificado && (
                                    <div className="w-full max-w-sm mb-6 p-4 bg-red-50 dark:bg-red-950/20 border-l-4 border-red-500 rounded-r-xl text-left">
                                        <p className="text-xs text-red-700/90 dark:text-red-300/90">
                                            O envio fica travado até o PDF existir. Gerar não mexe na cobrança: só desenha o boleto.
                                        </p>
                                        <button
                                            onClick={handleRegerarPdf}
                                            disabled={regenerandoPdf}
                                            className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RefreshCw size={16} className={regenerandoPdf ? 'animate-spin' : ''} />
                                            {regenerandoPdf ? 'Gerando PDF unificado...' : 'Gerar PDF unificado'}
                                        </button>
                                    </div>
                                )}

                                {/* Histórico da cobrança. Antes era um card único com o
                                    carimbo vivo (`emitido_por_nome`/`emitido_em`), que a reemissão
                                    sobrescreve — quem emitiu antes desaparecia da tela. Chamado do
                                    Ítalo, 28/08/2026. */}
                                <div className="w-full max-w-sm mb-8 text-left">
                                    <TimelineCobranca fatura={fatura} />
                                </div>

                                <div className="w-full flex flex-col gap-4">
                                    <button
                                        onClick={handleVisualizarBoleto}
                                        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-primary text-primary dark:text-primary px-6 py-4 rounded-xl shadow-sm hover:shadow-md hover:bg-primary/10 dark:hover:bg-gray-700 transition-all flex items-center gap-3 font-bold w-full justify-center group"
                                    >
                                        <ExternalLink size={20} className="group-hover:scale-110 transition-transform" />
                                        Visualizar Boleto / PDF
                                    </button>

                                    {podeRegerarPdf && !semPdfUnificado && (
                                        <button
                                            onClick={handleRegerarPdf}
                                            disabled={regenerandoPdf}
                                            title="Redesenha o PDF e sobrescreve no Storage. Não altera a cobrança no banco/gateway."
                                            className="bg-white dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 hover:border-primary dark:hover:border-primary text-gray-500 dark:text-gray-400 hover:text-primary px-6 py-2.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <RefreshCw size={14} className={regenerandoPdf && layoutEmGeracao !== 'v6' ? 'animate-spin' : ''} />
                                            {regenerandoPdf && layoutEmGeracao !== 'v6'
                                                ? 'Gerando PDF...'
                                                : 'Regerar e Substituir PDF'}
                                        </button>
                                    )}

                                    {/* Layout novo do boleto unificado, só para adm. Só nas faturas da Vigor:
                                        o caminho da Gran tem PDF próprio (layout da Gran + fatura da Equatorial). */}
                                    {podeVerLayoutV6 && !ehBoletoGran && (
                                        <button
                                            onClick={() => abrirPdfBoletoVigor('v6')}
                                            disabled={regenerandoPdf}
                                            title="Desenha esta fatura no layout novo do boleto unificado e abre numa nova aba. Só visualização — não altera a cobrança nem o PDF enviado ao cliente."
                                            className="bg-white dark:bg-gray-800 border border-dashed border-emerald-300 dark:border-emerald-800 hover:border-emerald-500 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 px-6 py-2.5 rounded-xl transition-all flex items-center gap-2 text-xs font-bold w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <Sparkles size={14} className={layoutEmGeracao === 'v6' ? 'animate-pulse' : ''} />
                                            {layoutEmGeracao === 'v6' ? 'Gerando PDF...' : 'Ver no layout novo (v6)'}
                                        </button>
                                    )}

                                    {/* Copy Buttons */}
                                    <div className="flex flex-col gap-4 w-full">
                                        {fatura.codigo_pix_boleto && (
                                            <div className="flex flex-col gap-1.5 hover:bg-white dark:hover:bg-gray-800/50 p-2 -mx-2 rounded-xl transition-colors text-left">
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Código PIX (Copia e Cola)</span>
                                                <div className="flex items-center gap-2">
                                                    <input 
                                                        type="text" 
                                                        readOnly 
                                                        value={fatura.codigo_pix_boleto}
                                                        className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                                                    />
                                                    <button 
                                                        onClick={() => handleCopy(fatura.codigo_pix_boleto!, 'Código PIX')}
                                                        className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                        title="Copiar PIX"
                                                    >
                                                        <Copy size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {fatura.linha_digitavel_boleto && (
                                            <div className="flex flex-col gap-1.5 hover:bg-white dark:hover:bg-gray-800/50 p-2 -mx-2 rounded-xl transition-colors text-left">
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest pl-1">Linha Digitável do Boleto</span>
                                                <div className="flex items-center gap-2">
                                                    <input 
                                                        type="text" 
                                                        readOnly 
                                                        value={fatura.linha_digitavel_boleto}
                                                        className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50"
                                                    />
                                                    <button 
                                                        onClick={() => handleCopy(fatura.linha_digitavel_boleto!, 'Linha Digitável')}
                                                        className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                                        title="Copiar Linha Digitável"
                                                    >
                                                        <Copy size={18} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : fatura.motivo_pular_calculo ? (
                            <div className="flex flex-col items-center justify-center text-center max-w-sm">
                                <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-500 rounded-full flex items-center justify-center mb-6">
                                    <Send size={40} className="opacity-80" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Envio Informativo</h3>
                                <p className="text-gray-500 dark:text-gray-400 mb-6 font-medium">
                                    Esta fatura foi marcada para envio sem cobrança (apenas comunicação).
                                </p>
                                
                                <div className="w-full max-w-sm bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-700 text-left">
                                    <div className="flex flex-col gap-3">
                                        <div>
                                            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-0.5">Motivo Informado</span>
                                            <p className="text-sm font-medium text-gray-900 dark:text-white">"{fatura.motivo_pular_calculo}"</p>
                                        </div>
                                        {fatura.pular_calculo_por_nome && (
                                            <div>
                                                <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-0.5">Ação Realizada Por</span>
                                                <p className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                                                    <Clock size={14} className="text-primary" />
                                                    {fatura.pular_calculo_por_nome}
                                                    {fatura.pular_calculo_em && ` em ${new Date(fatura.pular_calculo_em).toLocaleDateString('pt-BR')}`}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center text-center max-w-sm">
                                {/* Boleto de teste não tem PDF publicado (não passou pelo n8n):
                                    ele é desenhado sob demanda, com a marca d'água TESTE. */}
                                {fatura.ambiente === 'sandbox' ? (
                                    <>
                                        <div className="w-20 h-20 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mb-6">
                                            <FlaskConical size={40} />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Boleto de teste</h3>
                                        <p className="text-gray-500 dark:text-gray-400 mb-5">
                                            Nada foi registrado no banco, então não existe PDF publicado. O boleto é
                                            desenhado na hora, com o layout real e marca d'água de teste.
                                        </p>
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={handleRegerarPdf}
                                                disabled={regenerandoPdf}
                                                className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-bold hover:bg-amber-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                                            >
                                                <Eye size={16} />
                                                {layoutEmGeracao === 'legado' ? 'Gerando...' : 'Ver boleto'}
                                            </button>
                                            <button
                                                onClick={() => handleBaixarPdfTeste('legado')}
                                                disabled={regenerandoPdf}
                                                className="px-5 py-2.5 border-2 border-amber-400 text-amber-700 dark:text-amber-400 rounded-xl font-bold hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                                            >
                                                <Upload size={16} className="rotate-180" />
                                                Baixar PDF
                                            </button>
                                        </div>

                                        {/* Layout novo (boleto unificado): fica separado do bloco acima de
                                            propósito — é o desenho em avaliação, não o que o cliente recebe. */}
                                        {podeVerLayoutV6 && (
                                        <div className="mt-6 w-full rounded-xl border border-dashed border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-900/10 p-4 text-left">
                                            <div className="flex items-center gap-2 mb-1">
                                                <Sparkles size={16} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                <h4 className="text-sm font-bold text-emerald-800 dark:text-emerald-300">
                                                    Boleto unificado — layout novo
                                                </h4>
                                            </div>
                                            <p className="text-xs text-emerald-700/80 dark:text-emerald-400/70 mb-3">
                                                Desenha esta mesma fatura no layout que está sendo desenvolvido
                                                (A4, detalhamento agrupado, histórico e PIX). Só visualização — não
                                                altera a cobrança nem o que é enviado ao cliente.
                                            </p>

                                            <label className="flex items-center gap-2 mb-3 cursor-pointer select-none w-fit">
                                                <input
                                                    type="checkbox"
                                                    checked={seloBomPagador}
                                                    onChange={(e) => setSeloBomPagador(e.target.checked)}
                                                    className="w-4 h-4 rounded border-emerald-400 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-0"
                                                />
                                                <span className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                                                    Selo "Bom Pagador" no cabeçalho
                                                </span>
                                                <span className="text-[11px] text-emerald-700/60 dark:text-emerald-400/50">
                                                    (em produção, quem decide é o histórico da UC)
                                                </span>
                                            </label>

                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => abrirPdfBoletoVigor('v6', seloBomPagador)}
                                                    disabled={regenerandoPdf}
                                                    className="px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    <Eye size={16} />
                                                    {layoutEmGeracao === 'v6' ? 'Gerando...' : 'Ver no layout novo'}
                                                </button>
                                                <button
                                                    onClick={() => handleBaixarPdfTeste('v6', seloBomPagador)}
                                                    disabled={regenerandoPdf}
                                                    className="px-5 py-2.5 border-2 border-emerald-400 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400 rounded-xl font-bold hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    <Upload size={16} className="rotate-180" />
                                                    Baixar PDF
                                                </button>
                                            </div>
                                        </div>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 rounded-full flex items-center justify-center mb-6">
                                            <Ticket size={40} className="opacity-50" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">PDF Indisponível</h3>
                                        {pdfPerdido ? (
                                            <>
                                                <p className="text-gray-500 dark:text-gray-400 mb-1">
                                                    A cobrança <strong>{fatura.num_cobranca_boleto}</strong> foi registrada no banco, mas o
                                                    PDF não chegou a ser gerado.
                                                </p>
                                                <p className="text-xs text-gray-400 dark:text-gray-500 mb-5">
                                                    Não cancele nem emita de novo &mdash; o boleto já existe e o cliente pode pagá-lo.
                                                    Gerar o PDF aqui não altera valor, vencimento nem nosso número.
                                                </p>
                                                <button
                                                    onClick={handlePublicarPdfPerdido}
                                                    disabled={publicandoPdf}
                                                    className="px-5 py-2.5 bg-primary text-white rounded-xl font-bold hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
                                                >
                                                    <RefreshCw size={16} className={publicandoPdf ? 'animate-spin' : undefined} />
                                                    {publicandoPdf ? 'Gerando...' : 'Gerar PDF do boleto'}
                                                </button>
                                            </>
                                        ) : (
                                            <p className="text-gray-500 dark:text-gray-400">
                                                A URL do boleto não foi encontrada no registro dessa fatura.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="p-6 bg-white dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4 shrink-0 backdrop-blur-md">

                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        <button
                            onClick={() => setCancelConfirmation(true)}
                            disabled={loading}
                            className="text-red-500 bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-red-100 dark:border-red-900/30 w-full sm:w-auto justify-center"
                        >
                                <X size={16} /> Cancelar boleto
                            </button>
                            {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && fatura.num_cobranca_boleto && (
                                <button
                                    onClick={openProrrogar}
                                    disabled={loading}
                                    className="text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-amber-100 dark:border-amber-900/30 w-full sm:w-auto justify-center"
                                >
                                    <CalendarDays size={16} /> Prorrogar boleto
                                </button>
                            )}
                            {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onParcelar && (
                                <button
                                    onClick={() => onParcelar(fatura)}
                                    disabled={loading}
                                    className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/10 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-indigo-100 dark:border-indigo-900/30 w-full sm:w-auto justify-center"
                                >
                                    <Split size={16} /> Parcelar
                                </button>
                            )}
                            {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onQuitar && (
                                <button
                                    onClick={() => onQuitar(fatura)}
                                    disabled={loading}
                                    className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100 dark:hover:bg-teal-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-teal-100 dark:border-teal-900/30 w-full sm:w-auto justify-center"
                                >
                                    <BadgeCheck size={16} /> Quitar
                                </button>
                            )}
                            {/* Este é o único caminho para "Sincronizar status" na visão de
                                Kanban: lá o card não tem menu "..." — clicar abre este modal.
                                Ver docs/superpowers/specs/2026-09-02-sincronizacao-status-fatura-design.md */}
                            {['em aberto', 'vencido'].includes((fatura.status_boleto_vigor || '').trim().toLowerCase()) && onSincronizarStatus && (
                                <button
                                    onClick={() => onSincronizarStatus(fatura)}
                                    disabled={loading}
                                    className="text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100 dark:hover:bg-teal-900/20 px-4 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 border border-teal-100 dark:border-teal-900/30 w-full sm:w-auto justify-center"
                                >
                                    <RefreshCw size={16} /> Sincronizar
                                </button>
                            )}
                        </div>

                        {/* Fatura com boleto sempre manda o boleto. Antes, o motivo de um pulo já
                            desfeito ficava na fatura e o envio saía como "Comunicação", sem o boleto
                            (JUN e AGO/2026 da UC 345243701247). */}
                        {fatura.motivo_pular_calculo && !fatura.url_boleto ? (
                            <button
                                onClick={handlePreSendSkipToClient}
                                disabled={loading || envioBloqueadoPorEspelho}
                                title={envioBloqueadoPorEspelho ? 'Substitua o espelho pelo PDF definitivo antes de enviar' : undefined}
                                className="bg-yellow-500 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-yellow-500/30 hover:shadow-yellow-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed w-full sm:w-auto"
                            >
                                <Send size={18} /> Enviar Comunicação
                            </button>
                        ) : (
                            <button
                                onClick={handlePreSendToClient}
                                disabled={loading || !fatura.url_boleto || envioBloqueadoPorEspelho || semPdfUnificado}
                                title={fatura.ambiente === 'sandbox'
                                    ? 'Boleto de teste não vai para o cliente: ele não existe no banco e não é pagável.'
                                    : envioBloqueadoPorEspelho ? 'Substitua o espelho pelo PDF definitivo antes de enviar'
                                    : semPdfUnificado ? 'Gere o PDF unificado antes de enviar' : undefined}
                                className="bg-blue-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg shadow-blue-500/30 hover:shadow-blue-500/40 hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed w-full sm:w-auto"
                            >
                                <Send size={18} /> Enviar para o cliente
                            </button>
                        )}
                    </div>

                </div>
            </div>,
            document.body
        )
    );
};

export default BoletoEmitidoModal;
